// client/src/coms/views/canvas_parts/canvas_room_view_parts/importedBrushUtils.js

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function clamp01(value) {
  return clamp(Number(value) || 0, 0, 1);
}

function ext(path) {
  const clean = String(path || '').toLowerCase().split('?')[0].split('#')[0];
  const idx = clean.lastIndexOf('.');
  return idx === -1 ? '' : clean.slice(idx);
}

function fileBaseName(path) {
  return String(path || 'Imported brush').split('/').pop() || 'Imported brush';
}

function stripExtension(path) {
  return fileBaseName(path).replace(/\.[^.]+$/, '') || 'Imported brush';
}


export function inferImportedBrushCategory(name, sourcePath = '') {
  const text = String((name || '') + ' ' + (sourcePath || '')).toLowerCase();
  if (/eraser|erase|rubber/.test(text)) return 'Erasers';
  if (/ink|inking|liner|line|pen|marker/.test(text)) return 'Inking';
  if (/paint|gouache|oil|acryl|water|wet|wash/.test(text)) return 'Painting';
  if (/pencil|graphite|charcoal|sketch|chalk|pastel/.test(text)) return 'Sketching';
  if (/texture|grain|noise|speckle|splatter|spray|stipple/.test(text)) return 'Texture';
  if (/calligraphy|sumi|japan|china|kanji|brush/.test(text)) return 'Calligraphy';
  return 'Imported';
}

function dataUrlFromArrayBuffer(buffer, mime) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }

  return 'data:' + mime + ';base64,' + btoa(binary);
}

function mimeForExt(extension) {
  if (extension === '.png') return 'image/png';
  if (extension === '.jpg' || extension === '.jpeg') return 'image/jpeg';
  if (extension === '.webp') return 'image/webp';
  if (extension === '.svg') return 'image/svg+xml';
  return 'application/octet-stream';
}

function isPngBuffer(buffer) {
  const bytes = new Uint8Array(buffer || new ArrayBuffer(0));
  const signature = [137, 80, 78, 71, 13, 10, 26, 10];
  if (bytes.length < signature.length) return false;
  for (let i = 0; i < signature.length; i += 1) {
    if (bytes[i] !== signature[i]) return false;
  }
  return true;
}

function createKritaPresetThumbnailDataUrl(buffer) {
  return isPngBuffer(buffer) ? dataUrlFromArrayBuffer(buffer, 'image/png') : '';
}

function createCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(Number(width) || 1));
  canvas.height = Math.max(1, Math.round(Number(height) || 1));
  return canvas;
}

function imageDataToCanvas(imageData) {
  if (!imageData || !imageData.width || !imageData.height) return null;
  const canvas = createCanvas(imageData.width, imageData.height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load imported brush image'));
    image.src = src;
  });
}

async function imageDataFromDataUrl(dataUrl) {
  const image = await loadImage(dataUrl);
  const width = image.naturalWidth || image.width || 1;
  const height = image.naturalHeight || image.height || 1;
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(image, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height);
}

export function parseGbr(buffer) {
  const view = new DataView(buffer);

  if (buffer.byteLength < 32) {
    throw new Error('GBR too small');
  }

  const headerSize = view.getUint32(0, false);
  const version = view.getUint32(4, false);
  const width = view.getUint32(8, false);
  const height = view.getUint32(12, false);
  const bytes = view.getUint32(16, false);
  const magic =
    String.fromCharCode(view.getUint8(20)) +
    String.fromCharCode(view.getUint8(21)) +
    String.fromCharCode(view.getUint8(22)) +
    String.fromCharCode(view.getUint8(23));

  if (magic !== 'GIMP') {
    throw new Error('Not a GIMP GBR brush');
  }

  const spacing = version >= 2 && buffer.byteLength >= 28 ? view.getUint32(24, false) : 10;
  const dataOffset = headerSize;
  const pixelCount = width * height;

  if (!width || !height || width > 4096 || height > 4096) {
    throw new Error('Invalid GBR dimensions');
  }

  const src = new Uint8Array(buffer, dataOffset);
  const imageData = new ImageData(width, height);

  if (bytes === 1) {
    for (let i = 0; i < pixelCount; i += 1) {
      const v = src[i] || 0;
      imageData.data[i * 4 + 0] = v;
      imageData.data[i * 4 + 1] = v;
      imageData.data[i * 4 + 2] = v;
      imageData.data[i * 4 + 3] = 255;
    }
  } else if (bytes === 2) {
    for (let i = 0; i < pixelCount; i += 1) {
      const gray = src[i * 2] || 0;
      const alpha = src[i * 2 + 1] || 0;
      imageData.data[i * 4 + 0] = gray;
      imageData.data[i * 4 + 1] = gray;
      imageData.data[i * 4 + 2] = gray;
      imageData.data[i * 4 + 3] = alpha;
    }
  } else if (bytes === 3) {
    for (let i = 0; i < pixelCount; i += 1) {
      imageData.data[i * 4 + 0] = src[i * 3] || 0;
      imageData.data[i * 4 + 1] = src[i * 3 + 1] || 0;
      imageData.data[i * 4 + 2] = src[i * 3 + 2] || 0;
      imageData.data[i * 4 + 3] = 255;
    }
  } else if (bytes === 4) {
    for (let i = 0; i < pixelCount; i += 1) {
      imageData.data[i * 4 + 0] = src[i * 4] || 0;
      imageData.data[i * 4 + 1] = src[i * 4 + 1] || 0;
      imageData.data[i * 4 + 2] = src[i * 4 + 2] || 0;
      imageData.data[i * 4 + 3] = src[i * 4 + 3] || 0;
    }
  } else {
    throw new Error('Unsupported GBR bytes per pixel: ' + bytes);
  }

  return {
    headerSize,
    version,
    width,
    height,
    bytes,
    spacing,
    imageData,
  };
}

function parseGbrAt(buffer, offset) {
  const available = buffer.byteLength - offset;

  if (available < 32) {
    throw new Error('GBR chunk too small');
  }

  const view = new DataView(buffer, offset);
  const headerSize = view.getUint32(0, false);
  const width = view.getUint32(8, false);
  const height = view.getUint32(12, false);
  const bytes = view.getUint32(16, false);
  const magic =
    String.fromCharCode(view.getUint8(20)) +
    String.fromCharCode(view.getUint8(21)) +
    String.fromCharCode(view.getUint8(22)) +
    String.fromCharCode(view.getUint8(23));

  if (magic !== 'GIMP') {
    throw new Error('No GBR magic at offset');
  }

  if (!width || !height || width > 4096 || height > 4096 || ![1, 2, 3, 4].includes(bytes)) {
    throw new Error('Invalid embedded GBR dimensions');
  }

  const pixelBytes = width * height * bytes;
  const totalBytes = headerSize + pixelBytes;

  if (totalBytes <= 0 || offset + totalBytes > buffer.byteLength) {
    throw new Error('Embedded GBR chunk extends past file end');
  }

  const gbrBuffer = buffer.slice(offset, offset + totalBytes);
  const gbr = parseGbr(gbrBuffer);
  gbr.offset = offset;
  gbr.totalBytes = totalBytes;
  return gbr;
}

export function parseGih(buffer) {
  const bytes = new Uint8Array(buffer);
  const brushes = [];
  const candidates = [];

  for (let i = 0; i < Math.max(0, bytes.length - 24); i += 1) {
    if (bytes[i + 20] === 71 && bytes[i + 21] === 73 && bytes[i + 22] === 77 && bytes[i + 23] === 80) {
      candidates.push(i);
    }
  }

  let lastEnd = -1;

  candidates.forEach((offset) => {
    if (offset < lastEnd) return;

    try {
      const gbr = parseGbrAt(buffer, offset);
      brushes.push(gbr);
      lastEnd = offset + gbr.totalBytes;
    } catch {}
  });

  if (!brushes.length) {
    throw new Error('No embedded GBR brush images found inside GIH');
  }

  return {
    count: brushes.length,
    brushes,
  };
}

function analyseBrushImageMask(imageData) {
  const width = imageData && imageData.width ? imageData.width : 0;
  const height = imageData && imageData.height ? imageData.height : 0;
  const src = imageData && imageData.data ? imageData.data : null;
  const totalPixels = Math.max(1, width * height);
  let transparentPixels = 0;
  let semiTransparentPixels = 0;
  let lightOpaquePixels = 0;
  let nearWhiteOpaquePixels = 0;
  let darkOpaquePixels = 0;
  let nearBlackOpaquePixels = 0;
  let alphaWeightedDarkness = 0;
  let alphaWeight = 0;
  let nonGrayOpaquePixels = 0;
  let visiblePixels = 0;

  if (!src) {
    return {
      hasUsefulAlpha: false,
      isAllGray: true,
      looksLikeWhiteBackgroundMask: false,
      looksLikeBlackBackgroundMask: false,
      transparentRatio: 0,
      lightOpaqueRatio: 0,
      darkOpaqueRatio: 0,
      averageDarkness: 0,
    };
  }

  for (let i = 0; i < src.length; i += 4) {
    const a = src[i + 3];
    const r = src[i];
    const g = src[i + 1];
    const b = src[i + 2];
    const lum = r * 0.299 + g * 0.587 + b * 0.114;
    const darkness = clamp((255 - lum) / 255, 0, 1);

    if (a < 8) transparentPixels += 1;
    if (a < 245) semiTransparentPixels += 1;
    if (a > 200 && lum > 220) lightOpaquePixels += 1;
    if (a > 200 && lum > 246) nearWhiteOpaquePixels += 1;
    if (a > 8 && lum < 120) darkOpaquePixels += 1;
    if (a > 8 && lum < 28) nearBlackOpaquePixels += 1;
    if (a > 8) {
      alphaWeightedDarkness += darkness * (a / 255);
      alphaWeight += a / 255;
      visiblePixels += 1;
      if (Math.max(r, g, b) - Math.min(r, g, b) > 3) nonGrayOpaquePixels += 1;
    }
  }

  const transparentRatio = transparentPixels / totalPixels;
  const semiTransparentRatio = semiTransparentPixels / totalPixels;
  const lightOpaqueRatio = lightOpaquePixels / totalPixels;
  const nearWhiteOpaqueRatio = nearWhiteOpaquePixels / totalPixels;
  const darkOpaqueRatio = darkOpaquePixels / totalPixels;
  const nearBlackOpaqueRatio = nearBlackOpaquePixels / totalPixels;
  const hasUsefulAlpha = transparentRatio > 0.01 || semiTransparentRatio > 0.08;
  const averageDarkness = alphaWeight > 0 ? alphaWeightedDarkness / alphaWeight : 0;
  const isAllGray = visiblePixels <= 0 || nonGrayOpaquePixels / Math.max(1, visiblePixels) < 0.002;

  return {
    hasUsefulAlpha,
    isAllGray,
    looksLikeWhiteBackgroundMask: !hasUsefulAlpha && lightOpaqueRatio > 0.35 && darkOpaqueRatio > 0.002,
    looksLikeBlackBackgroundMask: !hasUsefulAlpha && nearBlackOpaqueRatio > 0.35 && nearWhiteOpaqueRatio > 0.002,
    transparentRatio,
    lightOpaqueRatio,
    darkOpaqueRatio,
    averageDarkness,
  };
}

function analyseSolidBackgroundBrushTip(imageData, options = {}) {
  const width = imageData && imageData.width ? imageData.width : 0;
  const height = imageData && imageData.height ? imageData.height : 0;
  const src = imageData && imageData.data ? imageData.data : null;
  if (!src || width < 2 || height < 2) {
    return { hasSolidBackground: false, r: 0, g: 0, b: 0, threshold: 0.08, strength: 0.24 };
  }

  let rSum = 0;
  let gSum = 0;
  let bSum = 0;
  let count = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (x > 1 && y > 1 && x < width - 2 && y < height - 2) continue;
      const i = (y * width + x) * 4;
      const a = src[i + 3];
      if (a < 220) continue;
      const adjustedRgb = getKritaAdjustedRgb(imageData, i, options);
      rSum += adjustedRgb.r;
      gSum += adjustedRgb.g;
      bSum += adjustedRgb.b;
      count += 1;
    }
  }

  if (count < Math.max(8, Math.ceil((width + height) * 0.5))) {
    return { hasSolidBackground: false, r: 0, g: 0, b: 0, threshold: 0.08, strength: 0.24 };
  }

  const r = rSum / count;
  const g = gSum / count;
  const b = bSum / count;
  let nearBackground = 0;
  let farFromBackground = 0;
  let opaque = 0;

  for (let i = 0; i < src.length; i += 4) {
    const a = src[i + 3];
    if (a < 220) continue;
    opaque += 1;
    const adjustedRgb = getKritaAdjustedRgb(imageData, i, options);
    const dist = Math.sqrt((adjustedRgb.r - r) ** 2 + (adjustedRgb.g - g) ** 2 + (adjustedRgb.b - b) ** 2) / 441.67295593;
    if (dist < 0.035) nearBackground += 1;
    if (dist > 0.11) farFromBackground += 1;
  }

  const totalOpaque = Math.max(1, opaque);
  return {
    hasSolidBackground: nearBackground / totalOpaque > 0.42 && farFromBackground / totalOpaque > 0.0008,
    r,
    g,
    b,
    threshold: 0.035,
    strength: 0.22,
  };
}

function resolveBrushTipMaskMode(imageData, options = {}) {
  const requestedMode = String(options.maskMode || '').trim().toLowerCase();
  if (requestedMode === 'alpha' || requestedMode === 'darkness' || requestedMode === 'lightness' || requestedMode === 'background-difference') return requestedMode;

  const stats = analyseBrushImageMask(imageData);
  const background = analyseSolidBackgroundBrushTip(imageData, options);

  if (options && options.colorAsMask === true) {
    if (background.hasSolidBackground && !stats.looksLikeWhiteBackgroundMask && !stats.looksLikeBlackBackgroundMask) return 'background-difference';
    return 'darkness';
  }

  if (options && options.colorAsMask === false) {
    if (stats.looksLikeWhiteBackgroundMask) return 'darkness';
    if (stats.looksLikeBlackBackgroundMask) return 'lightness';
    if (background.hasSolidBackground && !stats.hasUsefulAlpha) return 'background-difference';
    return 'alpha';
  }

  if (stats.looksLikeWhiteBackgroundMask) return 'darkness';
  if (stats.looksLikeBlackBackgroundMask) return 'lightness';
  if (stats.hasUsefulAlpha) return 'alpha';
  if (background.hasSolidBackground) return 'background-difference';
  return stats.averageDarkness < 0.5 ? 'darkness' : 'lightness';
}

function resolveBrushTipEdgeMode(options = {}) {
  const requested = String(options.edgeMode || options.maskEdgeMode || '').trim().toLowerCase();
  if (requested === 'hard' || requested === 'hard-edge' || requested === 'hardedge') return 'hard';
  if (requested === 'soft' || requested === 'normal') return 'normal';
  return 'normal';
}

function applyBrushTipEdgeMode(alpha, edgeMode) {
  const value = clamp01(alpha);
  if (edgeMode !== 'hard') return value;
  if (value < 0.055) return 0;
  return clamp01(Math.pow(value, 0.24));
}

function normaliseKritaBrushTipAdjustments(adjustments) {
  if (!adjustments || typeof adjustments !== 'object') return null;

  const midPoint = clamp(Number(adjustments.adjustmentMidPoint), 0, 255);
  const brightness = Number(adjustments.brightnessAdjustment);
  const contrast = Number(adjustments.contrastAdjustment);
  const autoAdjustMidPoint = adjustments.autoAdjustMidPoint === true;
  const hasUsefulAdjustment = autoAdjustMidPoint
    || (Number.isFinite(midPoint) && Math.abs(midPoint - 127) > 0.5)
    || (Number.isFinite(brightness) && Math.abs(brightness) > 0.0001)
    || (Number.isFinite(contrast) && Math.abs(contrast) > 0.0001);

  if (!hasUsefulAdjustment) return null;

  return {
    adjustmentMidPoint: Number.isFinite(midPoint) ? midPoint : 127,
    brightnessAdjustment: Number.isFinite(brightness) ? brightness : 0,
    contrastAdjustment: Number.isFinite(contrast) ? contrast : 0,
    autoAdjustMidPoint,
  };
}

function resolveKritaAutoAdjustmentMidPoint(imageData) {
  if (!imageData || !imageData.data || !imageData.width || !imageData.height) return 127;

  const data = imageData.data;
  let total = 0;
  let weight = 0;

  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3] / 255;
    if (alpha <= 0.003) continue;
    const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    total += lum * alpha;
    weight += alpha;
  }

  return weight > 0 ? clamp(total / weight, 1, 254) : 127;
}

function applyKritaBrushTipAdjustmentChannel(value, midpoint, adjustments) {
  let next = clamp(Number(value), 0, 255) / 255;
  const mid = clamp(Number(midpoint), 0, 255) / 255;
  const brightness = Number(adjustments && adjustments.brightnessAdjustment) || 0;
  const contrast = Number(adjustments && adjustments.contrastAdjustment) || 0;

  if (Math.abs(contrast) > 0.0001) {
    const factor = contrast >= 0
      ? 1 + (contrast * 4)
      : Math.max(0.02, 1 + contrast);
    next = ((next - mid) * factor) + mid;
  }

  if (Math.abs(brightness) > 0.0001) {
    next += brightness;
  }

  return clamp(Math.round(next * 255), 0, 255);
}

function getKritaAdjustedRgb(imageData, sourceOffset, options = {}) {
  const src = imageData.data;
  const adjustments = normaliseKritaBrushTipAdjustments(options.kritaAdjustments);

  if (!adjustments) {
    return {
      r: src[sourceOffset],
      g: src[sourceOffset + 1],
      b: src[sourceOffset + 2],
    };
  }

  const midpoint = adjustments.autoAdjustMidPoint
    ? resolveKritaAutoAdjustmentMidPoint(imageData)
    : adjustments.adjustmentMidPoint;

  return {
    r: applyKritaBrushTipAdjustmentChannel(src[sourceOffset], midpoint, adjustments),
    g: applyKritaBrushTipAdjustmentChannel(src[sourceOffset + 1], midpoint, adjustments),
    b: applyKritaBrushTipAdjustmentChannel(src[sourceOffset + 2], midpoint, adjustments),
  };
}

function buildAlphaValues(imageData, options = {}) {
  const width = imageData.width;
  const height = imageData.height;
  const src = imageData.data;
  const values = new Float32Array(width * height);
  const maskMode = resolveBrushTipMaskMode(imageData, options);
  const edgeMode = resolveBrushTipEdgeMode(options);
  const background = maskMode === 'background-difference' ? analyseSolidBackgroundBrushTip(imageData, options) : null;

  for (let i = 0, p = 0; i < src.length; i += 4, p += 1) {
    const sourceAlpha = src[i + 3] / 255;
    const adjustedRgb = getKritaAdjustedRgb(imageData, i, options);
    const r = adjustedRgb.r;
    const g = adjustedRgb.g;
    const b = adjustedRgb.b;
    const lum = r * 0.299 + g * 0.587 + b * 0.114;
    const darkness = clamp((252 - lum) / 252, 0, 1);
    const lightness = clamp(lum / 252, 0, 1);
    let alpha = sourceAlpha;

    if (maskMode === 'darkness') {
      alpha = Math.pow(darkness, 0.72) * sourceAlpha;
      if (lum > 246) alpha = 0;
    } else if (maskMode === 'lightness') {
      alpha = Math.pow(lightness, 0.72) * sourceAlpha;
      if (lum < 9) alpha = 0;
    } else if (maskMode === 'background-difference' && background && background.hasSolidBackground) {
      const distance = Math.sqrt((r - background.r) ** 2 + (g - background.g) ** 2 + (b - background.b) ** 2) / 441.67295593;
      alpha = Math.pow(clamp((distance - background.threshold) / background.strength, 0, 1), 0.62) * sourceAlpha;
    }

    alpha = applyBrushTipEdgeMode(alpha, edgeMode);
    values[p] = alpha < 0.015 ? 0 : clamp01(alpha);
  }

  return values;
}

function createAlphaCanvasFromImageData(imageData, options = {}) {
  const width = imageData.width;
  const height = imageData.height;
  const alphaValues = buildAlphaValues(imageData, options);
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  const out = ctx.createImageData(width, height);
  for (let i = 0; i < alphaValues.length; i += 1) {
    const a = Math.round(alphaValues[i] * 255);
    out.data[i * 4 + 0] = 0;
    out.data[i * 4 + 1] = 0;
    out.data[i * 4 + 2] = 0;
    out.data[i * 4 + 3] = a;
  }

  ctx.putImageData(out, 0, 0);
  return trimTransparentCanvas(canvas);
}

function trimTransparentCanvas(canvas) {
  if (!canvas) return null;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return canvas;
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < canvas.height; y += 1) {
    for (let x = 0; x < canvas.width; x += 1) {
      const a = data[(y * canvas.width + x) * 4 + 3];
      if (a > 2) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < minX || maxY < minY) return canvas;

  const pad = 2;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(canvas.width - 1, maxX + pad);
  maxY = Math.min(canvas.height - 1, maxY + pad);

  const width = Math.max(1, maxX - minX + 1);
  const height = Math.max(1, maxY - minY + 1);
  const out = createCanvas(width, height);
  const outCtx = out.getContext('2d', { willReadFrequently: true });
  if (!outCtx) return canvas;
  outCtx.drawImage(canvas, minX, minY, width, height, 0, 0, width, height);
  return out;
}

function limitImportedBrushCanvasSize(canvas, maxDimension = 768) {
  if (!canvas || !canvas.width || !canvas.height) return canvas || null;
  const limit = Math.max(32, Number(maxDimension) || 768);
  const largest = Math.max(canvas.width, canvas.height);
  if (largest <= limit) return canvas;
  const scale = limit / largest;
  const out = createCanvas(Math.max(1, Math.round(canvas.width * scale)), Math.max(1, Math.round(canvas.height * scale)));
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return canvas;
  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, out.width, out.height);
  return out;
}

function createProcreateGrainCanvasFromImageData(imageData, options = {}) {
  if (!imageData || !imageData.width || !imageData.height) return null;
  const maskMode = options && options.textureInverted === true ? 'darkness' : 'lightness';
  return limitImportedBrushCanvasSize(createAlphaCanvasFromImageData(imageData, { maskMode, edgeMode: 'normal' }) || imageDataToCanvas(imageData), 512);
}

function pickProcreateRotationMode(metadata) {
  if (!metadata) return 'stroke';
  const rollMode = Math.round(Number(metadata.shapeRollMode));
  if (metadata.shapeRandomise === true || rollMode === 2 || Math.abs(Number(metadata.shapeRotationJitter) || 0) > 0.01) return 'random';
  if (metadata.shapeAzimuth === true || metadata.oriented === true || metadata.shapeRoll === true || rollMode === 1) return 'stroke';
  return 'none';
}

function normaliseProcreateCurve(points, fallbackLow = 0, fallbackHigh = 1) {
  if (Array.isArray(points) && points.length >= 2) {
    return points
      .map((point) => {
        if (Array.isArray(point) && point.length >= 2) return { x: clamp(Number(point[0]) || 0, 0, 1), y: clamp(Number(point[1]) || 0, 0, 1) };
        if (point && typeof point === 'object') return { x: clamp(Number(point.x) || 0, 0, 1), y: clamp(Number(point.y) || 0, 0, 1) };
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => a.x - b.x);
  }

  return [
    { x: 0, y: clamp(Number(fallbackLow) || 0, 0, 1) },
    { x: 1, y: clamp(Number(fallbackHigh) || 1, 0, 4) },
  ];
}

function mapProcreateMetadataToImportedBrushPresetValues(metadata, source = {}) {
  const stamp = metadata && metadata.stamp === true;
  const spacing = stamp
    ? clamp(Number(metadata && metadata.plotSpacing) || 0.42, 0.06, 2)
    : clamp(Number(metadata && metadata.plotSpacing) || Number(source.spacing) || 0.08, 0.005, 2);
  const plotJitter = Math.abs(Number(metadata && metadata.plotJitter) || 0);
  const plotJitterLongitudinal = Math.abs(Number(metadata && metadata.plotJitterLongitudinal) || 0);
  const shapeScatter = Math.abs(Number(metadata && metadata.shapeScatter) || 0);
  const shapeScatterRoll = Math.abs(Number(metadata && metadata.shapeScatterRoll) || 0);
  const scatter = clamp((plotJitter + plotJitterLongitudinal + shapeScatter + shapeScatterRoll) * 2.2, 0, 4);
  const minSize = clamp(Number(metadata && metadata.minSize) || 0, 0, 1);
  const maxSize = Math.max(0.02, Number(metadata && metadata.maxSize) || 1);
  const sizePressure = Math.abs(Number(metadata && metadata.dynamicsPressureSize) || 0);
  const opacityPressure = Math.abs(Number(metadata && metadata.dynamicsPressureOpacity) || 0);
  const flowPressure = Math.abs(Number(metadata && metadata.dynamicsPressureBleed) || 0) + Math.abs(1 - (Number(metadata && metadata.dynamicsGlazedFlow) || 1));
  const pressureToSize = !stamp && (sizePressure > 0.01 || metadata && metadata.dynamicsPressureSizeCurve != null);
  const pressureToOpacity = !stamp && opacityPressure > 0.01;
  const pressureToFlow = !stamp && flowPressure > 0.01;
  const pressureToSpacing = !stamp && Math.abs(Number(metadata && metadata.plotSpacingSpeed) || 0) > 0.01;
  const paintSize = Number(metadata && metadata.paintSize);
  const tipScale = Number.isFinite(paintSize) && paintSize > 0 ? clamp(paintSize * 4.5, 0.04, 8) : Number(source.tipScale) || 1;
  const fixedAngle = Number.isFinite(Number(metadata && metadata.shapeAngle)) ? Number(metadata.shapeAngle) : (Number(metadata && metadata.shapeRotation) || 0) * Math.PI * 2;
  const paintOpacity = Number.isFinite(Number(metadata && metadata.paintOpacity)) ? Number(metadata.paintOpacity) : Number(metadata && metadata.maxOpacity);
  const flow = Number.isFinite(Number(metadata && metadata.dynamicsGlazedFlow)) ? Number(metadata.dynamicsGlazedFlow) : 1;
  const shapeCount = Math.max(1, Math.min(16, Math.round((Number(metadata && metadata.shapeCount) || 0) + 1)));
  const shapeCountJitter = clamp(Math.abs(Number(metadata && metadata.shapeCountJitter) || Number(metadata && metadata.shapeCountTilt) || Number(metadata && metadata.shapeCountRoll) || 0), 0, 1);
  return {
    spacing,
    scatter,
    rotationMode: pickProcreateRotationMode(metadata),
    pressureToSize,
    pressureToOpacity,
    pressureToFlow,
    pressureToSpacing,
    tipScale,
    fixedAngle,
    flow: clamp(Number.isFinite(flow) ? flow : 1, 0.01, 1),
    brushOpacity: clamp(Number.isFinite(paintOpacity) ? paintOpacity : 1, 0.01, 1),
    colorAsMask: true,
    maskMode: 'darkness',
    edgeMode: Number(metadata && metadata.alphaThresholdAmount) > 0.25 ? 'hard' : 'normal',
    sizeCurve: pressureToSize ? normaliseProcreateCurve(metadata && metadata.dynamicsPressureSizeCurve, minSize / Math.max(1, maxSize), 1) : [],
    opacityCurve: pressureToOpacity ? normaliseProcreateCurve(metadata && metadata.dynamicsPressureOpacityCurve, clamp(Number(metadata && metadata.minOpacity) || 0, 0, 1), 1) : [],
    flowCurve: pressureToFlow ? normaliseProcreateCurve(metadata && metadata.dynamicsPressureBleedCurve, 0.25, 1) : [],
    spacingCurve: pressureToSpacing ? [{ x: 0, y: 1.8 }, { x: 1, y: 0.85 }] : [],
    procreateShapeCount: shapeCount,
    procreateShapeCountJitter: shapeCountJitter,
    procreateShapeScatter: shapeScatter,
    procreatePlotJitter: plotJitter,
    procreateTextureScale: clamp(Number(metadata && metadata.textureScale) || 1, 0.05, 12),
    procreateTextureMovement: Number(metadata && metadata.textureMovement) || 0,
    procreateTextureInverted: metadata && metadata.textureInverted === true,
    procreateTextureDepth: clamp(Number(metadata && metadata.grainDepth) || 1, 0, 1),
    procreateWetEdgesAmount: clamp(Number(metadata && metadata.wetEdgesAmount) || 0, 0, 1),
    procreateBurntEdgesAmount: clamp(Number(metadata && metadata.burntEdgesAmount) || 0, 0, 1),
    procreateStamp: stamp,
  };
}

function sampleMask(alphaValues, width, height, x, y) {
  const xi = Math.round(x);
  const yi = Math.round(y);
  if (xi < 0 || yi < 0 || xi >= width || yi >= height) return 0;
  return alphaValues[yi * width + xi] || 0;
}

function seededUnit(seed) {
  const x = Math.sin((Number(seed) || 1) * 12.9898) * 43758.5453123;
  return x - Math.floor(x);
}

function createImportedBrushSvgPreview(alphaValues, width, height, maxSize = 32) {
  if (!alphaValues || !width || !height) return null;

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = alphaValues[y * width + x] || 0;
      if (alpha > 0.018) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < minX || maxY < minY) return null;

  const sourceWidth = Math.max(1, maxX - minX + 1);
  const sourceHeight = Math.max(1, maxY - minY + 1);
  const scale = Math.min(1, maxSize / Math.max(sourceWidth, sourceHeight));
  const previewWidth = Math.max(1, Math.round(sourceWidth * scale));
  const previewHeight = Math.max(1, Math.round(sourceHeight * scale));
  const candidates = [];
  let totalAlpha = 0;

  for (let py = 0; py < previewHeight; py += 1) {
    const y0 = minY + (py / previewHeight) * sourceHeight;
    const y1 = minY + ((py + 1) / previewHeight) * sourceHeight;
    const sampleY0 = Math.max(minY, Math.floor(y0));
    const sampleY1 = Math.min(maxY, Math.max(sampleY0, Math.ceil(y1) - 1));

    for (let px = 0; px < previewWidth; px += 1) {
      const x0 = minX + (px / previewWidth) * sourceWidth;
      const x1 = minX + ((px + 1) / previewWidth) * sourceWidth;
      const sampleX0 = Math.max(minX, Math.floor(x0));
      const sampleX1 = Math.min(maxX, Math.max(sampleX0, Math.ceil(x1) - 1));
      let total = 0;
      let count = 0;
      let peak = 0;

      for (let sy = sampleY0; sy <= sampleY1; sy += 1) {
        for (let sx = sampleX0; sx <= sampleX1; sx += 1) {
          const alpha = alphaValues[sy * width + sx] || 0;
          total += alpha;
          if (alpha > peak) peak = alpha;
          count += 1;
        }
      }

      const average = count ? total / count : 0;
      const alpha = Math.max(average, peak * 0.38);
      if (alpha <= 0.025) continue;

      const seed = ((px + 1) * 73856093) ^ ((py + 1) * 19349663) ^ ((width + 17) * 83492791) ^ ((height + 31) * 2654435761);
      const keepScore = alpha * (0.45 + seededUnit(seed) * 0.7);
      totalAlpha += alpha;
      candidates.push({
        x: px + 0.5 + (seededUnit(seed + 11) - 0.5) * 0.34,
        y: py + 0.5 + (seededUnit(seed + 23) - 0.5) * 0.34,
        r: 0.34 + seededUnit(seed + 37) * 0.78,
        a: clamp(0.08 + Math.pow(alpha, 0.74) * 0.62, 0.06, 0.78),
        score: keepScore,
      });
    }
  }

  if (!candidates.length) return null;

  const densityTarget = Math.round(clamp(totalAlpha * 0.46, 28, 220));
  const dotLimit = Math.max(24, Math.min(220, densityTarget));
  const dots = candidates
    .sort((a, b) => b.score - a.score)
    .slice(0, dotLimit)
    .sort((a, b) => (a.y - b.y) || (a.x - b.x))
    .map((dot) => ({
      x: Number(dot.x.toFixed(3)),
      y: Number(dot.y.toFixed(3)),
      r: Number(dot.r.toFixed(3)),
      a: Number(dot.a.toFixed(3)),
    }));

  return {
    type: 'svg-dots-32',
    width: previewWidth,
    height: previewHeight,
    dots,
  };
}

function createImportedBrushBitmapPreview(alphaValues, width, height, maxSize = 32) {
  if (!alphaValues || !width || !height) return null;

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = alphaValues[y * width + x] || 0;
      if (alpha > 0.015) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < minX || maxY < minY) return null;

  const sourceWidth = Math.max(1, maxX - minX + 1);
  const sourceHeight = Math.max(1, maxY - minY + 1);
  const scale = Math.min(1, maxSize / Math.max(sourceWidth, sourceHeight));
  const previewWidth = Math.max(1, Math.round(sourceWidth * scale));
  const previewHeight = Math.max(1, Math.round(sourceHeight * scale));
  const pixels = [];

  for (let py = 0; py < previewHeight; py += 1) {
    const y0 = minY + (py / previewHeight) * sourceHeight;
    const y1 = minY + ((py + 1) / previewHeight) * sourceHeight;
    const sampleY0 = Math.max(minY, Math.floor(y0));
    const sampleY1 = Math.min(maxY, Math.max(sampleY0, Math.ceil(y1) - 1));

    for (let px = 0; px < previewWidth; px += 1) {
      const x0 = minX + (px / previewWidth) * sourceWidth;
      const x1 = minX + ((px + 1) / previewWidth) * sourceWidth;
      const sampleX0 = Math.max(minX, Math.floor(x0));
      const sampleX1 = Math.min(maxX, Math.max(sampleX0, Math.ceil(x1) - 1));
      let total = 0;
      let count = 0;

      for (let sy = sampleY0; sy <= sampleY1; sy += 1) {
        for (let sx = sampleX0; sx <= sampleX1; sx += 1) {
          total += alphaValues[sy * width + sx] || 0;
          count += 1;
        }
      }

      pixels.push(Math.max(0, Math.min(255, Math.round((count ? total / count : 0) * 255))));
    }
  }

  return {
    width: previewWidth,
    height: previewHeight,
    pixels,
  };
}

function smoothBlobPoints(points, passes = 2) {
  let result = Array.isArray(points) ? points : [];
  for (let pass = 0; pass < passes; pass += 1) {
    result = result.map((point, index) => {
      const prev = result[(index - 1 + result.length) % result.length];
      const next = result[(index + 1) % result.length];
      return {
        x: (prev.x + point.x * 2 + next.x) / 4,
        y: (prev.y + point.y * 2 + next.y) / 4,
      };
    });
  }
  return result;
}

function fallbackBlob(width, height) {
  const points = [];
  const count = 40;
  const aspectX = width >= height ? 0.5 : (width / Math.max(1, height)) * 0.5;
  const aspectY = height >= width ? 0.5 : (height / Math.max(1, width)) * 0.5;

  for (let i = 0; i < count; i += 1) {
    const angle = (i / count) * Math.PI * 2;
    points.push({
      x: Math.cos(angle) * aspectX,
      y: Math.sin(angle) * aspectY,
    });
  }

  return {
    type: 'blob-tip',
    width: Math.max(1, Math.round(width || 1)),
    height: Math.max(1, Math.round(height || 1)),
    points,
    softness: 0.35,
    source: 'imported-preview',
  };
}

export function createImportedBrushBlobPreviewFromImageData(imageData, options = {}) {
  if (!imageData || !imageData.width || !imageData.height) return fallbackBlob(1, 1);

  const width = imageData.width;
  const height = imageData.height;
  const alphaValues = buildAlphaValues(imageData, options);
  const threshold = clamp(Number(options.threshold) || 0.08, 0.01, 0.98);
  const cx = (width - 1) / 2;
  const cy = (height - 1) / 2;
  const maxRadius = Math.hypot(Math.max(cx, width - cx), Math.max(cy, height - cy));
  const rayCount = Math.max(24, Math.min(96, Math.round(Number(options.rayCount) || 56)));
  const points = [];
  let visibleCount = 0;
  let edgeSoftnessTotal = 0;

  for (let i = 0; i < rayCount; i += 1) {
    const angle = (i / rayCount) * Math.PI * 2;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    let hitRadius = 0;
    let firstSoftRadius = null;

    for (let r = 0; r <= maxRadius; r += 1) {
      const x = cx + dx * r;
      const y = cy + dy * r;
      const alpha = sampleMask(alphaValues, width, height, x, y);

      if (alpha > threshold) {
        hitRadius = r;
        visibleCount += 1;
        if (firstSoftRadius == null && alpha < 0.85) firstSoftRadius = r;
      }
    }

    if (hitRadius <= 0) {
      hitRadius = Math.min(width, height) * 0.25;
    }

    if (firstSoftRadius != null && hitRadius > 0) {
      edgeSoftnessTotal += clamp((hitRadius - firstSoftRadius) / Math.max(1, hitRadius), 0, 1);
    }

    points.push({
      x: ((cx + dx * hitRadius) - cx) / Math.max(1, width),
      y: ((cy + dy * hitRadius) - cy) / Math.max(1, height),
    });
  }

  if (visibleCount <= rayCount * 2) return fallbackBlob(width, height);

  const svgPreview = createImportedBrushSvgPreview(alphaValues, width, height, 32);
  const bitmap = createImportedBrushBitmapPreview(alphaValues, width, height, 32);
  const smoothed = smoothBlobPoints(points, 2).map((point) => ({
    x: Number(point.x.toFixed(4)),
    y: Number(point.y.toFixed(4)),
  }));
  const softness = clamp(Number((edgeSoftnessTotal / rayCount).toFixed(3)), 0, 1);

  return {
    type: 'blob-tip',
    width,
    height,
    points: smoothed,
    svgPreview,
    bitmap,
    softness,
    source: 'imported-preview',
  };
}

export async function createImportedBrushBlobPreview(source) {
  if (!source) return fallbackBlob(1, 1);
  if (source.imageData) return createImportedBrushBlobPreviewFromImageData(source.imageData);
  if (source.dataUrl) {
    const imageData = await imageDataFromDataUrl(source.dataUrl);
    return createImportedBrushBlobPreviewFromImageData(imageData);
  }
  if (source.canvas) {
    const ctx = source.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return fallbackBlob(source.canvas.width, source.canvas.height);
    return createImportedBrushBlobPreviewFromImageData(ctx.getImageData(0, 0, source.canvas.width, source.canvas.height));
  }
  return fallbackBlob(1, 1);
}


function escapeImportedBrushSvgText(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function createImportedBrushSvgPreviewMarkup(brushPreview, options = {}) {
  const blob = sanitizeImportedBrushBlobPreview(brushPreview);
  const svgPreview = blob && blob.svgPreview ? blob.svgPreview : null;
  const dots = svgPreview && Array.isArray(svgPreview.dots) ? svgPreview.dots : [];
  if (!svgPreview || !dots.length) return '';

  const width = Math.max(1, Math.min(32, Math.round(Number(svgPreview.width) || 1)));
  const height = Math.max(1, Math.min(32, Math.round(Number(svgPreview.height) || 1)));
  const fill = escapeImportedBrushSvgText(options.fill || '#000000');
  const background = options.background == null ? '' : String(options.background);
  const backgroundRect = background
    ? `<rect x="0" y="0" width="${width}" height="${height}" fill="${escapeImportedBrushSvgText(background)}"/>`
    : '';
  const circles = dots.map((dot) => {
    const x = Math.max(-2, Math.min(34, Number(dot && dot.x) || 0));
    const y = Math.max(-2, Math.min(34, Number(dot && dot.y) || 0));
    const r = Math.max(0.05, Math.min(4, Number(dot && dot.r) || 0.5));
    const a = Math.max(0.01, Math.min(1, Number(dot && dot.a) || 0.25));
    return `<circle cx="${Number(x.toFixed(3))}" cy="${Number(y.toFixed(3))}" r="${Number(r.toFixed(3))}" fill="${fill}" fill-opacity="${Number(a.toFixed(3))}"/>`;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" shape-rendering="geometricPrecision">${backgroundRect}${circles}</svg>`;
}


function encodeImportedBrushByteString(bytes) {
  if (!bytes || !bytes.length || typeof btoa !== 'function') return '';
  let binary = '';
  const chunkSize = 8192;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.slice(offset, offset + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  try {
    return btoa(binary);
  } catch {
    return '';
  }
}

function decodeImportedBrushByteString(value) {
  const raw = String(value || '').trim();
  if (!raw || typeof atob !== 'function') return null;
  try {
    const binary = atob(raw);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index) & 255;
    }
    return bytes;
  } catch {
    return null;
  }
}

function quantizeImportedBrushPreviewValue(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(255, Math.round(((Math.max(min, Math.min(max, n)) - min) / (max - min || 1)) * 255)));
}

function unquantizeImportedBrushPreviewValue(value, min, max) {
  const n = Math.max(0, Math.min(255, Math.round(Number(value) || 0)));
  return min + (n / 255) * (max - min);
}

export function createImportedBrushSvgPreviewCode(brushPreview) {
  const blob = sanitizeImportedBrushBlobPreview(brushPreview);
  const svgPreview = blob && blob.svgPreview ? blob.svgPreview : null;
  const dots = svgPreview && Array.isArray(svgPreview.dots) ? svgPreview.dots.slice(0, 240) : [];
  if (!svgPreview || !dots.length) return '';

  const width = Math.max(1, Math.min(32, Math.round(Number(svgPreview.width) || 32)));
  const height = Math.max(1, Math.min(32, Math.round(Number(svgPreview.height) || 32)));
  const bytes = new Uint8Array(dots.length * 4);

  dots.forEach((dot, index) => {
    const offset = index * 4;
    bytes[offset] = quantizeImportedBrushPreviewValue(dot && dot.x, -2, 34);
    bytes[offset + 1] = quantizeImportedBrushPreviewValue(dot && dot.y, -2, 34);
    bytes[offset + 2] = quantizeImportedBrushPreviewValue(dot && dot.r, 0.05, 4);
    bytes[offset + 3] = quantizeImportedBrushPreviewValue(dot && dot.a, 0.01, 1);
  });

  const encoded = encodeImportedBrushByteString(bytes);
  return encoded ? `svgdots32:${width}:${height}:${encoded}` : '';
}

export function createImportedBrushSvgPreviewFromCode(code) {
  const raw = String(code || '').trim();
  const match = raw.match(/^svgdots32:(\d{1,2}):(\d{1,2}):([A-Za-z0-9+/=]+)$/);
  if (!match) return null;

  const width = Math.max(1, Math.min(32, Math.round(Number(match[1]) || 32)));
  const height = Math.max(1, Math.min(32, Math.round(Number(match[2]) || 32)));
  const bytes = decodeImportedBrushByteString(match[3]);
  if (!bytes || bytes.length < 4) return null;

  const dots = [];
  const dotCount = Math.min(240, Math.floor(bytes.length / 4));
  for (let index = 0; index < dotCount; index += 1) {
    const offset = index * 4;
    dots.push({
      x: Number(unquantizeImportedBrushPreviewValue(bytes[offset], -2, 34).toFixed(3)),
      y: Number(unquantizeImportedBrushPreviewValue(bytes[offset + 1], -2, 34).toFixed(3)),
      r: Number(unquantizeImportedBrushPreviewValue(bytes[offset + 2], 0.05, 4).toFixed(3)),
      a: Number(unquantizeImportedBrushPreviewValue(bytes[offset + 3], 0.01, 1).toFixed(3)),
    });
  }

  return dots.length ? { type: 'svg-dots-32', width, height, dots } : null;
}

export function sanitizeImportedBrushBlobPreview(blobPreview) {
  if (!blobPreview || typeof blobPreview !== 'object') return null;
  const rawPoints = Array.isArray(blobPreview.points) ? blobPreview.points : [];
  const points = rawPoints.slice(0, 128).map((point) => ({
    x: clamp(Number(point && point.x) || 0, -2, 2),
    y: clamp(Number(point && point.y) || 0, -2, 2),
  })).filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

  if (points.length < 3) return null;

  const rawSvgPreview = blobPreview.svgPreview && typeof blobPreview.svgPreview === 'object' ? blobPreview.svgPreview : null;
  const svgWidth = rawSvgPreview ? Math.max(1, Math.min(32, Math.round(Number(rawSvgPreview.width) || 1))) : 0;
  const svgHeight = rawSvgPreview ? Math.max(1, Math.min(32, Math.round(Number(rawSvgPreview.height) || 1))) : 0;
  const svgDots = rawSvgPreview && Array.isArray(rawSvgPreview.dots)
    ? rawSvgPreview.dots.slice(0, 240).map((dot) => ({
      x: clamp(Number(dot && dot.x) || 0, -2, 34),
      y: clamp(Number(dot && dot.y) || 0, -2, 34),
      r: clamp(Number(dot && dot.r) || 0.5, 0.05, 4),
      a: clamp(Number(dot && dot.a) || 0.25, 0.01, 1),
    })).filter((dot) => Number.isFinite(dot.x) && Number.isFinite(dot.y) && dot.a > 0)
    : [];
  const svgPreview = svgWidth > 0 && svgHeight > 0 && svgDots.length > 0
    ? { type: 'svg-dots-32', width: svgWidth, height: svgHeight, dots: svgDots }
    : null;

  const rawBitmap = blobPreview.bitmap && typeof blobPreview.bitmap === 'object' ? blobPreview.bitmap : null;
  const bitmapWidth = rawBitmap ? Math.max(1, Math.min(32, Math.round(Number(rawBitmap.width) || 1))) : 0;
  const bitmapHeight = rawBitmap ? Math.max(1, Math.min(32, Math.round(Number(rawBitmap.height) || 1))) : 0;
  const bitmapPixels = rawBitmap && Array.isArray(rawBitmap.pixels)
    ? rawBitmap.pixels.slice(0, bitmapWidth * bitmapHeight).map((value) => Math.max(0, Math.min(255, Math.round(Number(value) || 0))))
    : [];
  const bitmap = bitmapWidth > 0 && bitmapHeight > 0 && bitmapPixels.length === bitmapWidth * bitmapHeight
    ? { width: bitmapWidth, height: bitmapHeight, pixels: bitmapPixels }
    : null;

  return {
    type: 'blob-tip',
    width: Math.max(1, Math.min(4096, Math.round(Number(blobPreview.width) || 1))),
    height: Math.max(1, Math.min(4096, Math.round(Number(blobPreview.height) || 1))),
    points,
    svgPreview,
    bitmap,
    softness: clamp(Number(blobPreview.softness) || 0, 0, 1),
    source: 'imported-preview',
  };
}

export function isImportedLocalBrushPreset(brush) {
  return !!(brush && typeof brush === 'object' && brush.localOnly === true && brush.blobPreview);
}

export function sanitizeImportedBrushPresetForCollab(brush) {
  if (!isImportedLocalBrushPreset(brush)) return null;

  let sourceBlobPreview = brush.blobPreview;
  const sourceBitmap = sourceBlobPreview && sourceBlobPreview.bitmap;
  const sourceCanvas = brush.originalTipCanvas || (Array.isArray(brush.originalTipCanvases) ? brush.originalTipCanvases.find(Boolean) : null);

  if (!sourceBitmap && sourceCanvas && sourceCanvas.width && sourceCanvas.height) {
    try {
      const ctx = sourceCanvas.getContext('2d', { willReadFrequently: true });
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
        sourceBlobPreview = createImportedBrushBlobPreviewFromImageData(imageData);
      }
    } catch {
      sourceBlobPreview = brush.blobPreview;
    }
  }

  const blobPreview = sanitizeImportedBrushBlobPreview(sourceBlobPreview);
  if (!blobPreview) return null;

  const brushPreviewSvgMarkup = createImportedBrushSvgPreviewMarkup(blobPreview, { fill: '#000000' });
  const brushPreviewSvg = blobPreview && blobPreview.svgPreview ? blobPreview.svgPreview : null;
  const brushPreviewSvgCode = createImportedBrushSvgPreviewCode(blobPreview);
  const brushPreviewSvgDots = brushPreviewSvg && Array.isArray(brushPreviewSvg.dots) ? brushPreviewSvg.dots.length : 0;

  return {
    brushMode: 'imported-local-preview',
    brushName: String(brush.name || brush.label || brush.id || 'Imported brush'),
    brushPreview: blobPreview,
    brushPreviewSvg,
    brushPreviewSvgCode,
    brushPreviewSvgMarkup,
    importedBrushSvgMarkup: brushPreviewSvgMarkup,
    brushSvgMarkup: brushPreviewSvgMarkup,
    svgMarkup: brushPreviewSvgMarkup,
    brushPreviewSvgWidth: brushPreviewSvg && brushPreviewSvg.width ? brushPreviewSvg.width : 32,
    brushPreviewSvgHeight: brushPreviewSvg && brushPreviewSvg.height ? brushPreviewSvg.height : 32,
    brushPreviewSvgDots,
    spacing: clamp(Number(brush.spacing) || 0.12, 0.01, 2),
    scatter: clamp(Number(brush.scatter) || 0, 0, 4),
    rotationMode: ['none', 'stroke', 'random', 'tilt'].includes(brush.rotationMode) ? brush.rotationMode : 'stroke',
    pressureToSize: brush.pressureToSize !== false,
    pressureToOpacity: brush.pressureToOpacity === true,
    pressureToFlow: brush.pressureToFlow === true,
    tipScale: clamp(Number(brush.tipScale) || 1, 0.02, 8),
    fixedAngle: Number(brush.fixedAngle) || 0,
    flow: clamp(Number(brush.flow) || 1, 0.01, 1),
    brushOpacity: clamp(Number(brush.brushOpacity) || 1, 0.01, 1),
    pressureToSpacing: brush.pressureToSpacing === true,
    sizeCurve: Array.isArray(brush.sizeCurve) ? brush.sizeCurve : [],
    opacityCurve: Array.isArray(brush.opacityCurve) ? brush.opacityCurve : [],
    flowCurve: Array.isArray(brush.flowCurve) ? brush.flowCurve : [],
    spacingCurve: Array.isArray(brush.spacingCurve) ? brush.spacingCurve : [],
    procreateShapeCount: Math.max(1, Math.min(16, Math.round(Number(brush.procreateShapeCount) || 1))),
    procreateShapeCountJitter: clamp(Number(brush.procreateShapeCountJitter) || 0, 0, 1),
    procreateShapeScatter: clamp(Number(brush.procreateShapeScatter) || 0, 0, 4),
    procreatePlotJitter: clamp(Number(brush.procreatePlotJitter) || 0, 0, 4),
    procreateTextureScale: clamp(Number(brush.procreateTextureScale) || 1, 0.05, 12),
    procreateTextureMovement: Number(brush.procreateTextureMovement) || 0,
    procreateTextureDepth: clamp(Number(brush.procreateTextureDepth) || 0, 0, 1),
    procreateWetEdgesAmount: clamp(Number(brush.procreateWetEdgesAmount) || 0, 0, 1),
    procreateBurntEdgesAmount: clamp(Number(brush.procreateBurntEdgesAmount) || 0, 0, 1),
    procreateStamp: brush.procreateStamp === true,
    tipScale: clamp(Number(brush.tipScale) || 1, 0.02, 8),
    fixedAngle: Number(brush.fixedAngle) || 0,
  };
}


function parseBooleanText(value, fallback = false) {
  const text = String(value == null ? '' : value).trim().toLowerCase();
  if (text === 'true' || text === '1' || text === 'yes') return true;
  if (text === 'false' || text === '0' || text === 'no') return false;
  return fallback;
}

function parseNumberText(value, fallback = 0) {
  const number = Number(String(value == null ? '' : value).trim());
  return Number.isFinite(number) ? number : fallback;
}

function decodeXmlEntities(text) {
  return String(text || '')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function getXmlAttribute(text, attributeName) {
  const attributes = parseKritaAttributes(text);
  const key = String(attributeName || '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(attributes, key) ? attributes[key] : '';
}

function hasXmlAttribute(text, attributeName) {
  const attributes = parseKritaAttributes(text);
  const key = String(attributeName || '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(attributes, key);
}

function escapeRegExp(text) {
  return String(text || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getKritaParam(xmlText, name) {
  const escapedName = escapeRegExp(name);
  const cdataMatch = String(xmlText || '').match(new RegExp('<param\\b[^>]*name="' + escapedName + '"[^>]*>\\s*<!\\[CDATA\\[([\\s\\S]*?)\\]\\]>\\s*</param>', 'i'));
  if (cdataMatch) return cdataMatch[1];
  const textMatch = String(xmlText || '').match(new RegExp('<param\\b[^>]*name="' + escapedName + '"[^>]*>([\\s\\S]*?)</param>', 'i'));
  return textMatch ? decodeXmlEntities(textMatch[1].replace(/<[^>]*>/g, '').trim()) : '';
}



function parseKritaAttributes(text) {
  const attributes = {};
  const source = String(text || '');
  source.replace(/([A-Za-z_:\-][A-Za-z0-9_:\-.]*)="([\s\S]*?)"/g, (match, key, value) => {
    const decoded = decodeXmlEntities(value);
    attributes[key] = decoded;
    attributes[String(key).toLowerCase()] = decoded;
    return match;
  });
  return attributes;
}

function truncateKritaDebugText(text, maxLength = 900) {
  const value = String(text == null ? '' : text);
  if (value.length <= maxLength) return value;
  return value.slice(0, maxLength) + '… [' + value.length + ' chars]';
}

function parseKritaParamDebugValue(rawValue) {
  const value = decodeXmlEntities(String(rawValue == null ? '' : rawValue).trim());
  const text = value.trim();

  if (!text) return '';
  if (/^(true|false)$/i.test(text)) return /^true$/i.test(text);
  if (/^-?\d+(?:\.\d+)?$/.test(text)) {
    const number = Number(text);
    if (Number.isFinite(number)) return number;
  }
  if (text.includes('<') && text.includes('>')) {
    return truncateKritaDebugText(text, 1400);
  }
  return truncateKritaDebugText(text, 900);
}

function extractKritaAllParams(xmlText) {
  const source = String(xmlText || '');
  const params = {};
  const ordered = [];
  const regex = /<param\b([^>]*)>([\s\S]*?)<\/param>/gi;
  let match = null;

  while ((match = regex.exec(source))) {
    const attributes = parseKritaAttributes(match[1] || '');
    const name = attributes.name || attributes.id || 'param_' + ordered.length;
    let body = String(match[2] || '').trim();
    const cdata = body.match(/^<!\[CDATA\[([\s\S]*?)\]\]>$/i);
    if (cdata) body = cdata[1];
    const value = parseKritaParamDebugValue(body.replace(/<[^>]*>/g, (tag) => tag.startsWith('<curve') || tag.startsWith('</curve') ? tag : ''));
    const item = {
      name,
      attributes,
      value,
      rawLength: body.length,
    };
    ordered.push(item);
    if (Object.prototype.hasOwnProperty.call(params, name)) {
      if (!Array.isArray(params[name])) params[name] = [params[name]];
      params[name].push(value);
    } else {
      params[name] = value;
    }
  }

  return { params, ordered };
}

function extractKritaChildSensorDebug(sensorText) {
  const source = String(sensorText || '');
  if (!source) return [];
  const sensors = [];
  const regex = /<ChildSensor\b([^>]*)>([\s\S]*?)<\/ChildSensor>/gi;
  let match = null;

  while ((match = regex.exec(source))) {
    const attributes = parseKritaAttributes(match[1] || '');
    const body = match[2] || '';
    sensors.push({
      id: attributes.id || '',
      attributes,
      curve: extractFirstCurveText(body),
      curvePoints: parseKritaCurvePoints(extractFirstCurveText(body)),
      raw: truncateKritaDebugText(match[0], 1000),
    });
  }

  const paramsRegex = /<params\b([^>]*)>([\s\S]*?)<\/params>/gi;
  while ((match = paramsRegex.exec(source))) {
    const attributes = parseKritaAttributes(match[1] || '');
    const body = match[2] || '';
    sensors.push({
      id: attributes.id || '',
      attributes,
      curve: extractFirstCurveText(body),
      curvePoints: parseKritaCurvePoints(extractFirstCurveText(body)),
      raw: truncateKritaDebugText(match[0], 1000),
    });
  }

  return sensors;
}

function buildKritaRawPresetDebug(xmlText, brush, knownSettings = {}) {
  const source = String(xmlText || '');
  const presetStart = (source.match(/<Preset\b[^>]*>/i) || [])[0] || '';
  const brushStart = brush && brush.raw ? brush.raw : ((source.match(/<Brush\b[^>]*>/i) || [])[0] || '');
  const allParams = extractKritaAllParams(source);
  const sensorNames = ['SizeSensor', 'OpacitySensor', 'FlowSensor', 'SpacingSensor', 'RotationSensor', 'ScatterSensor', 'ColorRateSensor', 'HueSensor', 'SaturationSensor', 'ValueSensor', 'MixSensor'];
  const sensors = {};

  sensorNames.forEach((name) => {
    const sensorText = getKritaParam(source, name);
    if (!sensorText) return;
    sensors[name] = {
      id: parseKritaSensorId(sensorText),
      attributes: parseKritaAttributes((String(sensorText).match(/^<[^>]+>/) || [''])[0]),
      curve: extractFirstCurveText(sensorText),
      curvePoints: parseKritaCurvePoints(extractFirstCurveText(sensorText)),
      childSensors: extractKritaChildSensorDebug(sensorText),
      raw: truncateKritaDebugText(sensorText, 1800),
    };
  });

  const knownParamNames = new Set([
    'brush_definition',
    'PressureSize',
    'PressureOpacity',
    'PressureRotation',
    'PressureScatter',
    'SpacingValue',
    'ScatterValue',
    'FlowValue',
    'OpacityValue',
    'SizeSensor',
    'OpacitySensor',
    'FlowSensor',
    'SpacingSensor',
    'RotationSensor',
    'ScatterSensor',
    'SizecommonCurve',
    'OpacitycommonCurve',
    'FlowcommonCurve',
    'SpacingcommonCurve',
    'EraserMode',
    'Texture/Pattern/Enabled',
  ]);

  return {
    presetTag: {
      raw: presetStart,
      attributes: parseKritaAttributes(presetStart),
    },
    brushTag: {
      raw: brushStart,
      attributes: parseKritaAttributes(brushStart),
      parsed: brush || null,
    },
    knownSettings,
    sensors,
    allParams: allParams.params,
    orderedParams: allParams.ordered,
    unmappedParams: allParams.ordered
      .filter((item) => !knownParamNames.has(item.name))
      .map((item) => ({ name: item.name, value: item.value, attributes: item.attributes, rawLength: item.rawLength })),
    counts: {
      paramCount: allParams.ordered.length,
      unmappedParamCount: allParams.ordered.filter((item) => !knownParamNames.has(item.name)).length,
      sensorCount: Object.keys(sensors).length,
      xmlLength: source.length,
    },
  };
}

function parseKritaCurvePoints(text) {
  const raw = String(text || '').trim();
  if (!raw) return [];
  return raw.split(';').map((pair) => {
    const parts = pair.split(',');
    if (parts.length < 2) return null;
    const x = Number(parts[0]);
    const y = Number(parts[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x: clamp(x, 0, 1), y: clamp(y, 0, 1) };
  }).filter(Boolean).sort((a, b) => a.x - b.x);
}


function extractFirstCurveText(text) {
  const match = String(text || '').match(/<curve\b[^>]*>([\s\S]*?)<\/curve>/i);
  return match ? decodeXmlEntities(match[1].trim()) : '';
}

function extractKritaSensorCurve(sensorText, preferredIds = ['pressure']) {
  const source = String(sensorText || '');
  if (!source) return '';

  for (const id of preferredIds) {
    const escapedId = escapeRegExp(id);
    const childMatch = source.match(new RegExp('<ChildSensor\\b[^>]*id="' + escapedId + '"[^>]*>[\\s\\S]*?<\\/ChildSensor>', 'i'));
    const childCurve = childMatch ? extractFirstCurveText(childMatch[0]) : '';
    if (childCurve) return childCurve;
  }

  for (const id of preferredIds) {
    const escapedId = escapeRegExp(id);
    const paramsMatch = source.match(new RegExp('<params\\b[^>]*id="' + escapedId + '"[^>]*>[\\s\\S]*?<\\/params>', 'i'));
    const paramsCurve = paramsMatch ? extractFirstCurveText(paramsMatch[0]) : '';
    if (paramsCurve) return paramsCurve;
  }

  return extractFirstCurveText(source);
}

function getKritaSensorNumericAttribute(sensorText, attributeName, fallback = 0) {
  const value = getXmlAttribute(String(sensorText || ''), attributeName);
  return value === '' ? fallback : parseNumberText(value, fallback);
}

function parseKritaSensorId(text) {
  const source = String(text || '');
  return getXmlAttribute(source, 'id') || '';
}

function parseKritaMaskGenerator(brushText) {
  const maskStart = (String(brushText || '').match(/<MaskGenerator\b[^>]*>/i) || [])[0] || '';
  if (!maskStart) return null;
  return {
    raw: maskStart,
    id: getXmlAttribute(maskStart, 'id') || 'default',
    type: getXmlAttribute(maskStart, 'type') || 'circle',
    diameter: parseNumberText(getXmlAttribute(maskStart, 'diameter'), 64),
    ratio: parseNumberText(getXmlAttribute(maskStart, 'ratio'), 1),
    hfade: parseNumberText(getXmlAttribute(maskStart, 'hfade'), 0.5),
    vfade: parseNumberText(getXmlAttribute(maskStart, 'vfade'), 0.5),
    spikes: parseNumberText(getXmlAttribute(maskStart, 'spikes'), 2),
    antialiasEdges: parseBooleanText(getXmlAttribute(maskStart, 'antialiasEdges'), false),
    softnessCurve: getXmlAttribute(maskStart, 'softness_curve'),
  };
}

function parseKritaBrushDefinition(xmlText) {
  const definition = getKritaParam(xmlText, 'brush_definition');
  const brushText = definition || ((String(xmlText || '').match(/<Brush\b[\s\S]*?<\/Brush>/i) || [])[0] || (String(xmlText || '').match(/<Brush\b[^>]*>/i) || [])[0] || '');
  const brushStart = (String(brushText).match(/<Brush\b[^>]*>/i) || [])[0] || brushText;
  const maskGenerator = parseKritaMaskGenerator(brushText);
  const hasColorAsMaskAttribute = hasXmlAttribute(brushStart, 'ColorAsMask');
  const hasBrushApplicationAttribute = hasXmlAttribute(brushStart, 'brushApplication');
  const hasPreserveLightnessAttribute = hasXmlAttribute(brushStart, 'preserveLightness');
  const hasAutoAdjustMidPointAttribute = hasXmlAttribute(brushStart, 'AutoAdjustMidPoint');
  let adjustmentMidPoint = parseNumberText(getXmlAttribute(brushStart, 'AdjustmentMidPoint'), 127);
  let brightnessAdjustment = parseNumberText(getXmlAttribute(brushStart, 'BrightnessAdjustment'), 0);
  let contrastAdjustment = parseNumberText(getXmlAttribute(brushStart, 'ContrastAdjustment'), 0);
  const adjustmentVersion = parseNumberText(getXmlAttribute(brushStart, 'AdjustmentVersion'), 1);
  const autoAdjustMidPoint = parseBooleanText(getXmlAttribute(brushStart, 'AutoAdjustMidPoint'), false);

  if (adjustmentVersion < 2 && !hasAutoAdjustMidPointAttribute) {
    adjustmentMidPoint = clamp(127 + ((adjustmentMidPoint - 127) * 2), 0, 255);
    brightnessAdjustment *= 2;
    contrastAdjustment *= 2;
    if (contrastAdjustment < 0) {
      contrastAdjustment = (1 / (1 - contrastAdjustment)) - 1;
    }
  }

  return {
    raw: brushStart,
    rawDefinition: brushText,
    type: getXmlAttribute(brushStart, 'type'),
    filename: getXmlAttribute(brushStart, 'filename'),
    md5sum: getXmlAttribute(brushStart, 'md5sum'),
    spacing: parseNumberText(getXmlAttribute(brushStart, 'spacing'), 0.12),
    scale: parseNumberText(getXmlAttribute(brushStart, 'scale'), 1),
    angle: parseNumberText(getXmlAttribute(brushStart, 'angle'), 0),
    colorAsMask: parseBooleanText(getXmlAttribute(brushStart, 'ColorAsMask'), false),
    hasColorAsMaskAttribute,
    density: parseNumberText(getXmlAttribute(brushStart, 'density'), 1),
    randomness: parseNumberText(getXmlAttribute(brushStart, 'randomness'), 0),
    useAutoSpacing: parseBooleanText(getXmlAttribute(brushStart, 'useAutoSpacing'), false),
    autoSpacingCoeff: parseNumberText(getXmlAttribute(brushStart, 'autoSpacingCoeff'), 1),
    brushApplication: parseNumberText(getXmlAttribute(brushStart, 'brushApplication'), NaN),
    hasBrushApplicationAttribute,
    preserveLightness: parseBooleanText(getXmlAttribute(brushStart, 'preserveLightness'), false),
    hasPreserveLightnessAttribute,
    maskGenerator,
    adjustmentMidPoint,
    brightnessAdjustment,
    contrastAdjustment,
    adjustmentVersion,
    autoAdjustMidPoint,
    hasAutoAdjustMidPointAttribute,
  };
}

function parseKritaPresetXml(xmlText, sourcePath = '') {
  const presetStart = (String(xmlText || '').match(/<Preset\b[^>]*>/i) || [])[0] || '';
  const brush = parseKritaBrushDefinition(xmlText);
  const rotationSensor = getKritaParam(xmlText, 'RotationSensor');
  const rotationSensorId = parseKritaSensorId(rotationSensor);
  const scatterValue = parseNumberText(getKritaParam(xmlText, 'ScatterValue'), 0);
  const scatterSensor = getKritaParam(xmlText, 'ScatterSensor');
  const pressureScatter = parseBooleanText(getKritaParam(xmlText, 'PressureScatter'), /pressure/i.test(scatterSensor) && parseBooleanText(getKritaParam(xmlText, 'ScatterUseCurve'), false));
  const pressureRotation = parseBooleanText(getKritaParam(xmlText, 'PressureRotation'), false);
  const sizeSensor = getKritaParam(xmlText, 'SizeSensor');
  const opacitySensor = getKritaParam(xmlText, 'OpacitySensor');
  const flowSensor = getKritaParam(xmlText, 'FlowSensor');
  const spacingSensor = getKritaParam(xmlText, 'SpacingSensor');
  const sizeSensorCurve = extractKritaSensorCurve(sizeSensor, ['pressure']);
  const opacitySensorCurve = extractKritaSensorCurve(opacitySensor, ['pressure']);
  const flowSensorCurve = extractKritaSensorCurve(flowSensor, ['pressure']);
  const spacingSensorCurve = extractKritaSensorCurve(spacingSensor, ['pressure']);
  const pressureToSize = parseBooleanText(getKritaParam(xmlText, 'PressureSize'), /pressure/i.test(sizeSensor) && (parseBooleanText(getKritaParam(xmlText, 'SizeUseCurve'), true) || !!sizeSensorCurve));
  const pressureToOpacity = parseBooleanText(getKritaParam(xmlText, 'PressureOpacity'), /pressure/i.test(opacitySensor) && (parseBooleanText(getKritaParam(xmlText, 'OpacityUseCurve'), false) || !!opacitySensorCurve));
  const pressureToFlow = /pressure/i.test(flowSensor) && (parseBooleanText(getKritaParam(xmlText, 'FlowUseCurve'), false) || !!flowSensorCurve);
  const pressureToSpacing = /pressure/i.test(spacingSensor) && (parseBooleanText(getKritaParam(xmlText, 'SpacingUseCurve'), false) || !!spacingSensorCurve);
  const eraserMode = parseBooleanText(getKritaParam(xmlText, 'EraserMode'), /eraser/i.test(sourcePath));
  const textureEnabled = parseBooleanText(getKritaParam(xmlText, 'Texture/Pattern/Enabled'), false);
  let rotationMode = 'none';

  if (/drawingangle|direction|speed/i.test(rotationSensorId + ' ' + rotationSensor)) {
    rotationMode = 'stroke';
  } else if (/ascension|declination|tilt/i.test(rotationSensorId + ' ' + rotationSensor)) {
    rotationMode = 'tilt';
  } else if (/fuzzy|random/i.test(rotationSensorId + ' ' + rotationSensor)) {
    rotationMode = 'random';
  } else if (pressureRotation) {
    rotationMode = 'stroke';
  }

  const name = getXmlAttribute(presetStart, 'name') || stripExtension(sourcePath) || 'Imported Krita brush';
  const spacing = brush.spacing > 0 ? brush.spacing : parseNumberText(getKritaParam(xmlText, 'SpacingValue'), 0.12);
  const flow = parseNumberText(getKritaParam(xmlText, 'FlowValue'), 1);
  const opacity = parseNumberText(getKritaParam(xmlText, 'OpacityValue'), 1);
  const rotationAngleOffset = getKritaSensorNumericAttribute(rotationSensor, 'angleOffset', 0) * Math.PI / 180;

  return {
    name,
    sourcePath,
    paintopid: getXmlAttribute(presetStart, 'paintopid'),
    brush,
    spacing: clamp(spacing, 0.005, 2),
    scatter: pressureScatter ? clamp(scatterValue, 0, 4) : 0,
    rotationMode,
    pressureToSize,
    pressureToOpacity,
    pressureToFlow,
    pressureToSpacing,
    eraserMode,
    flow: clamp(flow || 1, 0.01, 1),
    opacity: clamp(opacity || 1, 0.01, 1),
    sizeCurve: parseKritaCurvePoints(sizeSensorCurve || getKritaParam(xmlText, 'SizecommonCurve')),
    opacityCurve: parseKritaCurvePoints(opacitySensorCurve || getKritaParam(xmlText, 'OpacitycommonCurve')),
    flowCurve: parseKritaCurvePoints(flowSensorCurve || getKritaParam(xmlText, 'FlowcommonCurve')),
    spacingCurve: parseKritaCurvePoints(spacingSensorCurve || getKritaParam(xmlText, 'SpacingcommonCurve')),
    rotationSensorId,
    rotationAngleOffset,
    textureEnabled,
    knownSettings: {
      brushType: brush.type,
      brushFilename: brush.filename,
      brushSpacing: brush.spacing,
      brushScale: brush.scale,
      brushAngleRadians: brush.angle,
      flow,
      opacity,
      pressureToSize,
      pressureToOpacity,
      pressureToFlow,
      pressureToSpacing,
      pressureScatter,
      pressureRotation,
      rotationSensorId,
      rotationAngleOffset,
      sizeSensorCurve,
      opacitySensorCurve,
      flowSensorCurve,
      spacingSensorCurve,
      textureEnabled,
      maskGenerator: brush.maskGenerator,
      adjustmentMidPoint: brush.adjustmentMidPoint,
      brightnessAdjustment: brush.brightnessAdjustment,
      contrastAdjustment: brush.contrastAdjustment,
      adjustmentVersion: brush.adjustmentVersion,
      autoAdjustMidPoint: brush.autoAdjustMidPoint,
    },
    rawDebug: buildKritaRawPresetDebug(xmlText, brush, {
      brushType: brush.type,
      brushFilename: brush.filename,
      brushSpacing: brush.spacing,
      brushScale: brush.scale,
      brushAngleRadians: brush.angle,
      flow,
      opacity,
      pressureToSize,
      pressureToOpacity,
      pressureToFlow,
      pressureToSpacing,
      pressureScatter,
      pressureRotation,
      rotationSensorId,
      rotationAngleOffset,
      sizeSensorCurve,
      opacitySensorCurve,
      flowSensorCurve,
      spacingSensorCurve,
      textureEnabled,
      maskGenerator: brush.maskGenerator,
      adjustmentMidPoint: brush.adjustmentMidPoint,
      brightnessAdjustment: brush.brightnessAdjustment,
      contrastAdjustment: brush.contrastAdjustment,
      adjustmentVersion: brush.adjustmentVersion,
      autoAdjustMidPoint: brush.autoAdjustMidPoint,
    }),
    category: inferImportedBrushCategory(name, sourcePath),
  };
}

async function inflateKritaZtxtPayload(payload) {
  if (!payload || !payload.length) return '';

  const DecompressionStreamCtor = typeof window !== 'undefined' && window.DecompressionStream ? window.DecompressionStream : null;
  if (typeof DecompressionStreamCtor === 'function') {
    const formats = ['deflate', 'deflate-raw'];
    for (const format of formats) {
      try {
        const stream = new Blob([payload]).stream().pipeThrough(new DecompressionStreamCtor(format));
        const text = await new Response(stream).text();
        if (text && text.includes('<Preset')) return text;
      } catch {}
    }
  }

  if (typeof window !== 'undefined' && window.pako && typeof window.pako.inflate === 'function') {
    try {
      return window.pako.inflate(payload, { to: 'string' });
    } catch {}
  }

  return '';
}

function readUint32Be(bytes, offset) {
  return ((bytes[offset] << 24) >>> 0) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3];
}

export async function extractKritaPresetXmlFromKppBuffer(buffer) {
  const bytes = new Uint8Array(buffer || new ArrayBuffer(0));
  const pngSignature = [137, 80, 78, 71, 13, 10, 26, 10];
  let isPng = bytes.length >= 8;
  for (let i = 0; i < pngSignature.length && isPng; i += 1) {
    if (bytes[i] !== pngSignature[i]) isPng = false;
  }

  if (!isPng) {
    const text = new TextDecoder('utf-8').decode(bytes);
    return text.includes('<Preset') ? text : '';
  }

  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const length = readUint32Be(bytes, offset);
    const type = String.fromCharCode(bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7]);
    const dataStart = offset + 8;
    const dataEnd = dataStart + length;
    if (dataEnd + 4 > bytes.length) break;

    if (type === 'zTXt') {
      let separator = -1;
      for (let i = dataStart; i < dataEnd; i += 1) {
        if (bytes[i] === 0) {
          separator = i;
          break;
        }
      }

      if (separator > dataStart && separator + 2 < dataEnd) {
        const keyword = new TextDecoder('latin1').decode(bytes.slice(dataStart, separator));
        const compressionMethod = bytes[separator + 1];
        const payload = bytes.slice(separator + 2, dataEnd);
        if (compressionMethod === 0 && /preset/i.test(keyword)) {
          const inflated = await inflateKritaZtxtPayload(payload);
          if (inflated && inflated.includes('<Preset')) return inflated;
        }
      }
    } else if (type === 'tEXt' || type === 'iTXt') {
      const rawText = new TextDecoder('utf-8').decode(bytes.slice(dataStart, dataEnd));
      if (rawText.includes('<Preset')) return rawText.slice(rawText.indexOf('<Preset'));
    }

    offset = dataEnd + 4;
  }

  return '';
}

function normalizeAssetKey(path) {
  return String(path || '').toLowerCase().replace(/\\/g, '/');
}

function arrayBufferFromBase64(base64Text) {
  const clean = String(base64Text || '').replace(/\s+/g, '');
  if (!clean) return null;

  try {
    const binary = atob(clean);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  } catch {
    return null;
  }
}

function extractKritaEmbeddedResourceAssets(xmlText) {
  const source = String(xmlText || '');
  const assets = [];
  const regex = /<resource\b([^>]*)>\s*<!\[CDATA\[([\s\S]*?)\]\]>\s*<\/resource>/gi;
  let match = null;

  while ((match = regex.exec(source))) {
    const attributes = parseKritaAttributes(match[1] || '');
    const type = String(attributes.type || '').toLowerCase();
    const filename = attributes.filename || attributes.name || '';
    const buffer = arrayBufferFromBase64(match[2] || '');

    if (!filename || !buffer) continue;
    if (type !== 'brushes' && type !== 'patterns' && type !== 'textures') continue;

    assets.push({
      path: type + '/' + filename,
      name: filename,
      buffer,
      md5sum: attributes.md5sum || '',
      resourceType: type,
      embedded: true,
    });
  }

  return assets;
}

function findKritaBrushAsset(assetsByPath, filename, md5sum = '') {
  if (!assetsByPath || (!filename && !md5sum)) return null;
  const wanted = normalizeAssetKey(filename);
  const wantedBase = fileBaseName(wanted).toLowerCase();
  const wantedMd5 = String(md5sum || '').toLowerCase();
  const entries = (Array.isArray(assetsByPath) ? assetsByPath : Object.values(assetsByPath))
    .filter((asset) => asset && (!asset.resourceType || String(asset.resourceType).toLowerCase() === 'brushes'));

  if (wantedMd5) {
    const byMd5 = entries.find((asset) => String(asset.md5sum || '').toLowerCase() === wantedMd5);
    if (byMd5) return byMd5;
  }

  return entries.find((asset) => normalizeAssetKey(asset && asset.path) === wanted)
    || entries.find((asset) => normalizeAssetKey(asset && asset.name) === wanted)
    || entries.find((asset) => fileBaseName(asset && asset.path).toLowerCase() === wantedBase)
    || entries.find((asset) => fileBaseName(asset && asset.name).toLowerCase() === wantedBase)
    || entries.find((asset) => normalizeAssetKey(asset && asset.path).endsWith('/' + wantedBase))
    || null;
}


function createKritaAutoBrushImageData(brush) {
  const mask = brush && brush.maskGenerator ? brush.maskGenerator : null;
  const diameter = clamp(Number(mask && mask.diameter) || 64, 2, 1024);
  const ratio = clamp(Number(mask && mask.ratio) || 1, 0.05, 8);
  const width = Math.max(2, Math.ceil(diameter * Math.max(1, ratio)) + 4);
  const height = Math.max(2, Math.ceil(diameter * Math.max(1, 1 / ratio)) + 4);
  const imageData = new ImageData(width, height);
  const cx = (width - 1) / 2;
  const cy = (height - 1) / 2;
  const rx = Math.max(1, diameter * Math.max(1, ratio) * 0.5);
  const ry = Math.max(1, diameter * Math.max(1, 1 / ratio) * 0.5);
  const generatorId = String(mask && mask.id ? mask.id : 'default').toLowerCase();
  const hfade = clamp(Number(mask && mask.hfade) || 0.5, 0, 1);
  const vfade = clamp(Number(mask && mask.vfade) || 0.5, 0, 1);
  const fade = clamp((hfade + vfade) * 0.5, 0, 1);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const nx = (x - cx) / rx;
      const ny = (y - cy) / ry;
      const distance = Math.sqrt(nx * nx + ny * ny);
      let alpha = 0;

      if (distance <= 1) {
        if (generatorId === 'soft') {
          alpha = Math.pow(Math.max(0, 1 - distance), 1.25 + fade * 2.5);
        } else if (generatorId === 'gauss') {
          alpha = Math.exp(-(distance * distance) * (2.5 + fade * 2.5));
        } else {
          const edge = clamp((1 - distance) / Math.max(0.001, 0.08 + fade * 0.42), 0, 1);
          alpha = edge;
        }
      }

      const offset = (y * width + x) * 4;
      const value = Math.round(clamp(alpha, 0, 1) * 255);
      imageData.data[offset] = 0;
      imageData.data[offset + 1] = 0;
      imageData.data[offset + 2] = 0;
      imageData.data[offset + 3] = value;
    }
  }

  return imageData;
}

function createImportedBrushSourceFromKritaAutoBrush(kritaPreset) {
  const brush = kritaPreset && kritaPreset.brush ? kritaPreset.brush : null;
  const imageData = createKritaAutoBrushImageData(brush || {});
  return {
    name: kritaPreset && kritaPreset.name ? kritaPreset.name : 'Krita auto brush',
    sourcePath: kritaPreset && kritaPreset.sourcePath ? kritaPreset.sourcePath : '',
    originalTipDataUrl: '',
    originalTipCanvas: createAlphaCanvasFromImageData(imageData, { colorAsMask: true }) || imageDataToCanvas(imageData),
    originalTipCanvases: [],
    imageData,
    blobPreview: createImportedBrushBlobPreviewFromImageData(imageData, { colorAsMask: true }),
    spacing: brush && brush.spacing ? brush.spacing : 0.1,
  };
}

function createMissingKritaTipFallbackImageData(kritaPreset) {
  const brush = kritaPreset && kritaPreset.brush ? kritaPreset.brush : null;
  const mask = brush && brush.maskGenerator ? brush.maskGenerator : null;
  const diameter = clamp(Number(mask && mask.diameter) || 48, 4, 512);
  const ratio = clamp(Number(mask && mask.ratio) || 1, 0.08, 8);
  const width = Math.max(8, Math.ceil(diameter * Math.max(1, ratio)) + 8);
  const height = Math.max(8, Math.ceil(diameter * Math.max(1, 1 / ratio)) + 8);
  const imageData = new ImageData(width, height);
  const cx = (width - 1) / 2;
  const cy = (height - 1) / 2;
  const rx = Math.max(1, (width - 6) * 0.5);
  const ry = Math.max(1, (height - 6) * 0.5);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const nx = (x - cx) / rx;
      const ny = (y - cy) / ry;
      const distance = Math.sqrt(nx * nx + ny * ny);
      const feather = clamp((1 - distance) / 0.16, 0, 1);
      const offset = (y * width + x) * 4;
      const alpha = distance <= 1 ? feather : 0;
      imageData.data[offset] = 0;
      imageData.data[offset + 1] = 0;
      imageData.data[offset + 2] = 0;
      imageData.data[offset + 3] = Math.round(alpha * 255);
    }
  }

  return imageData;
}

async function createImportedBrushSourceFromKppPreview(buffer, kritaPreset, sourcePath) {
  const previewDataUrl = dataUrlFromArrayBuffer(buffer, 'image/png');
  const imageData = createMissingKritaTipFallbackImageData(kritaPreset);
  const tipCanvas = createAlphaCanvasFromImageData(imageData, { colorAsMask: true }) || imageDataToCanvas(imageData);
  return {
    name: kritaPreset && kritaPreset.name ? kritaPreset.name : stripExtension(sourcePath) || 'Krita brush fallback',
    sourcePath: sourcePath || '',
    originalTipDataUrl: '',
    thumbnailDataUrl: previewDataUrl,
    originalTipCanvas: tipCanvas,
    originalTipCanvases: [],
    imageData,
    blobPreview: createImportedBrushBlobPreviewFromImageData(imageData, { colorAsMask: true }),
    spacing: kritaPreset && Number(kritaPreset.spacing) ? kritaPreset.spacing : 0.12,
    missingLinkedBrushTip: kritaPreset && kritaPreset.missingLinkedBrushTip ? kritaPreset.missingLinkedBrushTip : '',
  };
}

async function createImportedBrushSourceFromAsset(asset) {
  if (!asset || !asset.buffer) throw new Error('Missing linked brush tip asset');
  const extension = ext(asset.path || asset.name);
  const name = stripExtension(asset.path || asset.name);

  if (['.png', '.jpg', '.jpeg', '.webp', '.svg'].includes(extension)) {
    const dataUrl = dataUrlFromArrayBuffer(asset.buffer, mimeForExt(extension));
    const imageData = await imageDataFromDataUrl(dataUrl);
    return {
      name,
      sourcePath: asset.path || asset.name,
      originalTipDataUrl: dataUrl,
      originalTipCanvas: createAlphaCanvasFromImageData(imageData, { colorAsMask: true }),
      imageData,
      blobPreview: createImportedBrushBlobPreviewFromImageData(imageData, { colorAsMask: true }),
      spacing: 0.12,
    };
  }

  if (extension === '.gbr') {
    const gbr = parseGbr(asset.buffer);
    return {
      name,
      sourcePath: asset.path || asset.name,
      originalTipDataUrl: '',
      originalTipCanvas: createAlphaCanvasFromImageData(gbr.imageData, { colorAsMask: true }) || imageDataToCanvas(gbr.imageData),
      imageData: gbr.imageData,
      blobPreview: createImportedBrushBlobPreviewFromImageData(gbr.imageData, { colorAsMask: true }),
      spacing: (Number(gbr.spacing) || 12) / 100,
    };
  }

  if (extension === '.gih') {
    const gih = parseGih(asset.buffer);
    const canvases = (gih.brushes || []).map((brush) => createAlphaCanvasFromImageData(brush.imageData, { colorAsMask: true }) || imageDataToCanvas(brush.imageData)).filter(Boolean);
    const first = gih.brushes && gih.brushes[0] ? gih.brushes[0] : null;
    if (!first || !first.imageData) throw new Error('GIH did not contain a usable brush image');
    return {
      name,
      sourcePath: asset.path || asset.name,
      originalTipDataUrl: '',
      originalTipCanvas: canvases[0] || null,
      originalTipCanvases: canvases,
      originalTipImageDatas: (gih.brushes || []).map((brush) => brush && brush.imageData).filter(Boolean),
      imageData: first.imageData,
      blobPreview: createImportedBrushBlobPreviewFromImageData(first.imageData, { colorAsMask: true }),
      spacing: (Number(first.spacing) || 12) / 100,
    };
  }

  throw new Error('Unsupported linked brush tip type: ' + (extension || asset.path || asset.name));
}


function isSupportedKritaPaintop(kritaPreset) {
  const paintopid = String(kritaPreset && kritaPreset.paintopid ? kritaPreset.paintopid : '').toLowerCase();
  return !paintopid || paintopid === 'paintbrush';
}

function getKritaPresetHardnessValue(kritaPreset) {
  const debug = String(kritaPreset && kritaPreset.rawDebug ? kritaPreset.rawDebug : '');
  const candidates = [
    'SharpnessValue',
    'Sharpness/value',
    'sharpness',
    'HardnessValue',
    'Hardness/value',
    'BrushTip/Sharpness',
  ];

  for (const key of candidates) {
    const value = parseNumberText(getKritaParam(debug, key), NaN);
    if (Number.isFinite(value)) return value;
  }

  const sharpnessMatch = debug.match(/(?:Sharpness|Hardness)[^\n\r<>=]*[=:\s]+([0-9]*\.?[0-9]+)/i);
  if (sharpnessMatch) {
    const value = Number(sharpnessMatch[1]);
    if (Number.isFinite(value)) return value;
  }

  return NaN;
}

function getKritaBrushTipAdjustments(kritaPreset) {
  const brush = kritaPreset && kritaPreset.brush ? kritaPreset.brush : null;
  if (!brush) return null;
  return normaliseKritaBrushTipAdjustments({
    adjustmentMidPoint: brush.adjustmentMidPoint,
    brightnessAdjustment: brush.brightnessAdjustment,
    contrastAdjustment: brush.contrastAdjustment,
    autoAdjustMidPoint: brush.autoAdjustMidPoint,
  });
}

const KRITA_BRUSH_APPLICATION_ALPHA_MASK = 0;
const KRITA_BRUSH_APPLICATION_IMAGE_STAMP = 1;
const KRITA_BRUSH_APPLICATION_LIGHTNESS_MAP = 2;
const KRITA_BRUSH_APPLICATION_GRADIENT_MAP = 3;

function getKritaBrushApplicationName(application) {
  if (application === KRITA_BRUSH_APPLICATION_IMAGE_STAMP) return 'image-stamp';
  if (application === KRITA_BRUSH_APPLICATION_LIGHTNESS_MAP) return 'lightness-map';
  if (application === KRITA_BRUSH_APPLICATION_GRADIENT_MAP) return 'gradient-map';
  return 'alpha-mask';
}

function resolveKritaBrushApplication(kritaPreset, source) {
  const brush = kritaPreset && kritaPreset.brush ? kritaPreset.brush : {};
  const type = String(brush.type || '').toLowerCase();
  if (type === 'auto_brush') return KRITA_BRUSH_APPLICATION_ALPHA_MASK;

  const stats = source && source.imageData ? analyseBrushImageMask(source.imageData) : null;
  const hasColorAndTransparency = Boolean(stats && !stats.isAllGray);
  const legacyBrushApplication = (forceColorToAlpha) => {
    if (hasColorAndTransparency && forceColorToAlpha !== true) return KRITA_BRUSH_APPLICATION_IMAGE_STAMP;
    return KRITA_BRUSH_APPLICATION_ALPHA_MASK;
  };

  if (brush.hasPreserveLightnessAttribute) {
    return brush.preserveLightness === true ? KRITA_BRUSH_APPLICATION_LIGHTNESS_MAP : legacyBrushApplication(brush.colorAsMask);
  }

  if (brush.hasBrushApplicationAttribute) {
    const application = Math.round(Number(brush.brushApplication));
    if (application === KRITA_BRUSH_APPLICATION_IMAGE_STAMP || application === KRITA_BRUSH_APPLICATION_LIGHTNESS_MAP || application === KRITA_BRUSH_APPLICATION_GRADIENT_MAP) return application;
    return KRITA_BRUSH_APPLICATION_ALPHA_MASK;
  }

  if (brush.hasColorAsMaskAttribute) return legacyBrushApplication(brush.colorAsMask);
  return legacyBrushApplication(false);
}

function getKritaBrushTipEdgeMode(kritaPreset) {
  const name = String(kritaPreset && kritaPreset.name ? kritaPreset.name : '');
  const sourcePath = String(kritaPreset && kritaPreset.sourcePath ? kritaPreset.sourcePath : '');
  const brushFilename = String(kritaPreset && kritaPreset.brush && kritaPreset.brush.filename ? kritaPreset.brush.filename : '');
  const haystack = (name + ' ' + sourcePath + ' ' + brushFilename).toLowerCase();
  const hardnessValue = getKritaPresetHardnessValue(kritaPreset);

  if (/\bsoft\b|soft[ _-]?edge|blur|smudge|water|wash/.test(haystack)) return 'normal';
  if (/hard[ _-]?(edge|edged|ness)?|hardedge|block|square|chisel/.test(haystack)) return 'hard';
  if (Number.isFinite(hardnessValue) && hardnessValue >= 0.82) return 'hard';
  if (kritaPreset && kritaPreset.brush && Number(kritaPreset.brush.brushApplication) === 1 && !kritaPreset.textureEnabled) return 'hard';
  return 'normal';
}

function getKritaBrushTipMaskMode(kritaPreset, source, kritaAdjustments = null) {
  const application = resolveKritaBrushApplication(kritaPreset, source);
  const stats = source && source.imageData ? analyseBrushImageMask(source.imageData) : null;
  const background = source && source.imageData ? analyseSolidBackgroundBrushTip(source.imageData, { kritaAdjustments }) : null;

  if (application === KRITA_BRUSH_APPLICATION_IMAGE_STAMP) {
    if (stats && stats.hasUsefulAlpha) return 'alpha';
    if (background && background.hasSolidBackground) return 'background-difference';
    return 'alpha';
  }

  if (application === KRITA_BRUSH_APPLICATION_LIGHTNESS_MAP || application === KRITA_BRUSH_APPLICATION_GRADIENT_MAP) {
    if (stats && stats.hasUsefulAlpha) return 'alpha';
    if (background && background.hasSolidBackground) return 'background-difference';
    if (stats && stats.looksLikeBlackBackgroundMask) return 'lightness';
    if (stats && stats.looksLikeWhiteBackgroundMask) return 'darkness';
    return 'alpha';
  }

  if (stats && stats.hasUsefulAlpha && !stats.looksLikeWhiteBackgroundMask && !stats.looksLikeBlackBackgroundMask) return 'alpha';
  if (stats && stats.looksLikeWhiteBackgroundMask) return 'darkness';
  if (stats && stats.looksLikeBlackBackgroundMask) return 'lightness';
  if (background && background.hasSolidBackground) return 'background-difference';
  return 'darkness';
}

function createAlphaCanvasesFromImageDatas(imageDatas, options = {}) {
  if (!Array.isArray(imageDatas)) return [];
  return imageDatas
    .map((imageData) => createAlphaCanvasFromImageData(imageData, options) || imageDataToCanvas(imageData))
    .filter(Boolean);
}

function createBlobPreviewFromFirstImageData(imageDatas, fallbackImageData, options = {}) {
  const first = Array.isArray(imageDatas) && imageDatas.length ? imageDatas[0] : fallbackImageData;
  return first ? createImportedBrushBlobPreviewFromImageData(first, options) : '';
}

export async function createImportedBrushPresetFromKritaKppBuffer(buffer, sourcePath = '', assetsByPath = []) {
  const kritaPresetThumbnailDataUrl = createKritaPresetThumbnailDataUrl(buffer);
  const xmlText = await extractKritaPresetXmlFromKppBuffer(buffer);
  if (!xmlText) throw new Error('Could not extract Krita preset XML');
  const kritaPreset = parseKritaPresetXml(xmlText, sourcePath);
  const embeddedAssets = extractKritaEmbeddedResourceAssets(xmlText);
  const availableAssets = [
    ...(Array.isArray(assetsByPath) ? assetsByPath : Object.values(assetsByPath || {})),
    ...embeddedAssets,
  ];

  if (!isSupportedKritaPaintop(kritaPreset)) {
    throw new Error('Skipped unsupported Krita preset paintop: ' + (kritaPreset.paintopid || 'unknown') + '. Imported Pen Brush only supports paintbrush presets for now.');
  }

  const linkedAsset = findKritaBrushAsset(availableAssets, kritaPreset.brush && kritaPreset.brush.filename, kritaPreset.brush && kritaPreset.brush.md5sum);
  let source = null;

  if (linkedAsset) {
    source = await createImportedBrushSourceFromAsset(linkedAsset);
  } else if (kritaPreset.brush && kritaPreset.brush.type === 'auto_brush') {
    source = createImportedBrushSourceFromKritaAutoBrush(kritaPreset);
  } else {
    kritaPreset.missingLinkedBrushTip = (kritaPreset.brush && kritaPreset.brush.filename) || (kritaPreset.brush && kritaPreset.brush.type) || 'unknown';
    source = await createImportedBrushSourceFromKppPreview(buffer, kritaPreset, sourcePath);
  }

  const brushTipEdgeMode = getKritaBrushTipEdgeMode(kritaPreset);
  const brushTipAdjustments = getKritaBrushTipAdjustments(kritaPreset);
  const brushTipMaskMode = getKritaBrushTipMaskMode(kritaPreset, source, brushTipAdjustments);
  const brushTipOptions = { maskMode: brushTipMaskMode, edgeMode: brushTipEdgeMode, kritaAdjustments: brushTipAdjustments };
  const kritaBrushApplication = resolveKritaBrushApplication(kritaPreset, source);
  const kritaBrushImageStats = source && source.imageData ? analyseBrushImageMask(source.imageData) : null;
  const sourceTipImageDatas = Array.isArray(source && source.originalTipImageDatas) ? source.originalTipImageDatas.filter(Boolean) : [];
  const processedTipCanvases = sourceTipImageDatas.length ? createAlphaCanvasesFromImageDatas(sourceTipImageDatas, brushTipOptions) : [];

  return createImportedBrushPreset({
    id: 'krita-' + Date.now() + '-' + Math.random().toString(16).slice(2),
    name: kritaPreset.name,
    sourcePath: sourcePath || linkedAsset && (linkedAsset.path || linkedAsset.name) || source.sourcePath,
    originalTipDataUrl: source.originalTipDataUrl,
    thumbnailDataUrl: kritaPresetThumbnailDataUrl || source.thumbnailDataUrl,
    originalTipCanvas: processedTipCanvases[0] || (source.imageData ? createAlphaCanvasFromImageData(source.imageData, brushTipOptions) || source.originalTipCanvas : source.originalTipCanvas),
    originalTipCanvases: processedTipCanvases.length ? processedTipCanvases : source.originalTipCanvases,
    imageData: source.imageData,
    blobPreview: createBlobPreviewFromFirstImageData(sourceTipImageDatas, source.imageData, brushTipOptions) || source.blobPreview,
    spacing: kritaPreset.spacing || source.spacing,
    category: kritaPreset.category,
    scatter: kritaPreset.scatter,
    rotationMode: kritaPreset.rotationMode,
    pressureToSize: kritaPreset.pressureToSize,
    pressureToOpacity: kritaPreset.pressureToOpacity,
    pressureToFlow: kritaPreset.pressureToFlow,
    pressureToSpacing: kritaPreset.pressureToSpacing,
    tipScale: kritaPreset.brush && kritaPreset.brush.type === 'auto_brush' ? 1 : kritaPreset.brush && kritaPreset.brush.scale,
    fixedAngle: (kritaPreset.brush && kritaPreset.brush.angle ? kritaPreset.brush.angle : 0) + (kritaPreset.rotationAngleOffset || 0),
    colorAsMask: brushTipMaskMode !== 'alpha',
    maskMode: brushTipMaskMode,
    edgeMode: brushTipEdgeMode,
    flow: kritaPreset.flow,
    brushOpacity: kritaPreset.opacity,
    sizeCurve: kritaPreset.sizeCurve,
    opacityCurve: kritaPreset.opacityCurve,
    flowCurve: kritaPreset.flowCurve,
    spacingCurve: kritaPreset.spacingCurve,
    rotationSensorId: kritaPreset.rotationSensorId,
    kritaBrushApplication,
    kritaBrushApplicationName: getKritaBrushApplicationName(kritaBrushApplication),
    kritaAdjustments: brushTipAdjustments,
    kritaBrushHasUsefulAlpha: Boolean(kritaBrushImageStats && kritaBrushImageStats.hasUsefulAlpha),
    kritaBrushIsAllGray: Boolean(kritaBrushImageStats && kritaBrushImageStats.isAllGray),
    brushSourcePath: linkedAsset ? (linkedAsset.path || linkedAsset.name || '') : source.sourcePath,
    brushSourceEmbedded: Boolean(linkedAsset && linkedAsset.embedded),
    brushSourceMd5: linkedAsset ? (linkedAsset.md5sum || '') : '',
    kritaPreset,
  });
}

export async function createImportedBrushPresetFromAssetBuffer(path, buffer) {
  const source = await createImportedBrushSourceFromAsset({ path, name: fileBaseName(path), buffer });
  return createImportedBrushPreset({
    name: source.name,
    sourcePath: source.sourcePath,
    originalTipDataUrl: source.originalTipDataUrl,
    thumbnailDataUrl: source.thumbnailDataUrl,
    originalTipCanvas: source.originalTipCanvas,
    originalTipCanvases: source.originalTipCanvases,
    imageData: source.imageData,
    blobPreview: source.blobPreview,
    spacing: source.spacing,
  });
}

export function createImportedBrushPreset({ id, name, originalTipDataUrl, thumbnailDataUrl, originalTipCanvas, originalTipCanvases, imageData, blobPreview, spacing = 0.12, scatter = 0, rotationMode = 'stroke', pressureToSize = true, pressureToOpacity = false, pressureToFlow = false, pressureToSpacing = false, category = '', sourcePath = '', tipScale = 1, fixedAngle = 0, colorAsMask = true, maskMode = '', edgeMode = '', flow = 1, brushOpacity = 1, sizeCurve = [], opacityCurve = [], flowCurve = [], spacingCurve = [], rotationSensorId = '', kritaBrushApplication = null, kritaBrushApplicationName = '', kritaBrushHasUsefulAlpha = false, kritaBrushIsAllGray = false, kritaAdjustments = null, kritaPreset = null, originalGrainCanvas = null, procreatePreset = null, procreateGrainSourcePath = '', procreateShapeCount = 1, procreateShapeCountJitter = 0, procreateShapeScatter = 0, procreatePlotJitter = 0, procreateTextureScale = 1, procreateTextureMovement = 0, procreateTextureInverted = false, procreateTextureDepth = 0, procreateWetEdgesAmount = 0, procreateBurntEdgesAmount = 0, procreateStamp = false }) {
  const safeKritaAdjustments = normaliseKritaBrushTipAdjustments(kritaAdjustments);
  const tipCanvas = originalTipCanvas || createAlphaCanvasFromImageData(imageData, { colorAsMask, kritaAdjustments: safeKritaAdjustments });
  return {
    id: id || 'imported-brush-' + Date.now() + '-' + Math.random().toString(16).slice(2),
    name: name || 'Imported brush',
    localOnly: true,
    category: category || inferImportedBrushCategory(name, sourcePath),
    sourcePath: sourcePath || '',
    originalTipDataUrl: originalTipDataUrl || '',
    thumbnailDataUrl: thumbnailDataUrl || '',
    originalTipCanvas: tipCanvas || null,
    originalTipCanvases: Array.isArray(originalTipCanvases) ? originalTipCanvases.filter(Boolean) : [],
    blobPreview: blobPreview || createImportedBrushBlobPreviewFromImageData(imageData),
    spacing: clamp(Number(spacing) || 0.12, 0.01, 2),
    scatter: clamp(Number(scatter) || 0, 0, 4),
    rotationMode: ['none', 'stroke', 'random', 'tilt'].includes(rotationMode) ? rotationMode : 'stroke',
    pressureToSize: pressureToSize !== false,
    pressureToOpacity: pressureToOpacity === true,
    pressureToFlow: pressureToFlow === true,
    pressureToSpacing: pressureToSpacing === true,
    tipScale: clamp(Number(tipScale) || 1, 0.02, 8),
    fixedAngle: Number(fixedAngle) || 0,
    colorAsMask: colorAsMask !== false,
    maskMode: String(maskMode || '').trim(),
    edgeMode: String(edgeMode || '').trim(),
    flow: clamp(Number(flow) || 1, 0.01, 1),
    brushOpacity: clamp(Number(brushOpacity) || 1, 0.01, 1),
    sizeCurve: Array.isArray(sizeCurve) ? sizeCurve : [],
    opacityCurve: Array.isArray(opacityCurve) ? opacityCurve : [],
    flowCurve: Array.isArray(flowCurve) ? flowCurve : [],
    spacingCurve: Array.isArray(spacingCurve) ? spacingCurve : [],
    rotationSensorId: String(rotationSensorId || ''),
    kritaBrushApplication: Number.isFinite(Number(kritaBrushApplication)) ? Number(kritaBrushApplication) : null,
    kritaBrushApplicationName: String(kritaBrushApplicationName || ''),
    kritaBrushHasUsefulAlpha: Boolean(kritaBrushHasUsefulAlpha),
    kritaBrushIsAllGray: Boolean(kritaBrushIsAllGray),
    kritaAdjustments: safeKritaAdjustments,
    kritaPreset: kritaPreset || null,
    originalGrainCanvas: originalGrainCanvas || null,
    procreatePreset: procreatePreset || null,
    procreateGrainSourcePath: String(procreateGrainSourcePath || ''),
    procreateShapeCount: Math.max(1, Math.min(16, Math.round(Number(procreateShapeCount) || 1))),
    procreateShapeCountJitter: clamp(Number(procreateShapeCountJitter) || 0, 0, 1),
    procreateShapeScatter: clamp(Number(procreateShapeScatter) || 0, 0, 4),
    procreatePlotJitter: clamp(Number(procreatePlotJitter) || 0, 0, 4),
    procreateTextureScale: clamp(Number(procreateTextureScale) || 1, 0.05, 12),
    procreateTextureMovement: Number(procreateTextureMovement) || 0,
    procreateTextureInverted: procreateTextureInverted === true,
    procreateTextureDepth: clamp(Number(procreateTextureDepth) || 0, 0, 1),
    procreateWetEdgesAmount: clamp(Number(procreateWetEdgesAmount) || 0, 0, 1),
    procreateBurntEdgesAmount: clamp(Number(procreateBurntEdgesAmount) || 0, 0, 1),
    procreateStamp: procreateStamp === true,
  };
}

export async function createImportedBrushPresetFromProcreateBrushParts({ shapePath = '', shapeBuffer = null, grainPath = '', grainBuffer = null, metadata = null, name = '', category = '' } = {}) {
  if (!shapeBuffer) throw new Error('Missing Procreate Shape.png');
  const source = await createImportedBrushSourceFromAsset({ path: shapePath, name: fileBaseName(shapePath), buffer: shapeBuffer });
  const mapped = mapProcreateMetadataToImportedBrushPresetValues(metadata, source);
  const shapeOptions = { maskMode: mapped.maskMode, edgeMode: mapped.edgeMode, colorAsMask: true };
  const originalTipCanvas = limitImportedBrushCanvasSize(createAlphaCanvasFromImageData(source.imageData, shapeOptions) || source.originalTipCanvas, 768);
  let originalGrainCanvas = null;

  if (grainBuffer) {
    try {
      const grainSource = await createImportedBrushSourceFromAsset({ path: grainPath || 'Grain.png', name: fileBaseName(grainPath || 'Grain.png'), buffer: grainBuffer });
      originalGrainCanvas = createProcreateGrainCanvasFromImageData(grainSource.imageData, mapped);
    } catch {
      originalGrainCanvas = null;
    }
  }

  return createImportedBrushPreset({
    id: 'procreate-' + Date.now() + '-' + Math.random().toString(16).slice(2),
    name: name || metadata && metadata.name || stripExtension(shapePath) || 'Procreate brush',
    sourcePath: shapePath,
    originalTipDataUrl: source.originalTipDataUrl,
    thumbnailDataUrl: source.thumbnailDataUrl,
    originalTipCanvas,
    originalTipCanvases: [originalTipCanvas].filter(Boolean),
    imageData: source.imageData,
    blobPreview: createImportedBrushBlobPreviewFromImageData(source.imageData, shapeOptions) || source.blobPreview,
    category: category || inferImportedBrushCategory(name || metadata && metadata.name || shapePath, shapePath),
    ...mapped,
    originalGrainCanvas,
    procreatePreset: metadata || null,
    procreateGrainSourcePath: grainPath || '',
  });
}

export async function createImportedBrushPresetFromFile(file) {
  if (!file) throw new Error('No brush file selected');
  const extension = ext(file.name);
  const name = stripExtension(file.name);

  if (['.png', '.jpg', '.jpeg', '.webp', '.svg'].includes(extension)) {
    const buffer = await file.arrayBuffer();
    const dataUrl = dataUrlFromArrayBuffer(buffer, mimeForExt(extension));
    const imageData = await imageDataFromDataUrl(dataUrl);
    const blobPreview = createImportedBrushBlobPreviewFromImageData(imageData);
    const originalTipCanvas = createAlphaCanvasFromImageData(imageData);
    return createImportedBrushPreset({ name, sourcePath: file.name, originalTipDataUrl: dataUrl, originalTipCanvas, imageData, blobPreview });
  }

  if (extension === '.gbr') {
    const gbr = parseGbr(await file.arrayBuffer());
    const originalTipCanvas = createAlphaCanvasFromImageData(gbr.imageData) || imageDataToCanvas(gbr.imageData);
    const blobPreview = createImportedBrushBlobPreviewFromImageData(gbr.imageData);
    return createImportedBrushPreset({ name, sourcePath: file.name, originalTipDataUrl: '', originalTipCanvas, imageData: gbr.imageData, blobPreview, spacing: (Number(gbr.spacing) || 12) / 100 });
  }

  if (extension === '.gih') {
    const gih = parseGih(await file.arrayBuffer());
    const first = gih.brushes && gih.brushes[0] ? gih.brushes[0] : null;
    if (!first || !first.imageData) throw new Error('GIH did not contain a usable brush image');
    const originalTipCanvases = (gih.brushes || []).map((brush) => createAlphaCanvasFromImageData(brush.imageData) || imageDataToCanvas(brush.imageData)).filter(Boolean);
    const originalTipCanvas = originalTipCanvases[0] || null;
    const blobPreview = createImportedBrushBlobPreviewFromImageData(first.imageData);
    return createImportedBrushPreset({ name, sourcePath: file.name, originalTipDataUrl: '', originalTipCanvas, originalTipCanvases, imageData: first.imageData, blobPreview, spacing: (Number(first.spacing) || 12) / 100 });
  }

  throw new Error('Unsupported local brush tip type: ' + (extension || file.name));
}
